import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { isDevAffordanceEnabled } from '../../utils/devMode';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { db, functions } from '../../../firebase';
import { doc, onSnapshot as onDocSnapshot, collection, onSnapshot, query, where, getDocs } from 'firebase/firestore';
import {
  subscribeProviderFormInstances,
  instancesById,
  profileSharedForRules,
  nonHfpInstanceIds,
} from '../../utils/providerFormInstances';
import { httpsCallable } from 'firebase/functions';
import { RefreshIcon, LightningBoltIcon, SearchIcon, LockClosedIcon, ChevronRightIcon, ChevronDownIcon, DocumentTextIcon, DownloadIcon, ExclamationIcon, UserIcon } from '@heroicons/react/outline';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { CheckCircleIcon } from '@heroicons/react/solid';
import RiskStratificationDialog from '../../components/provider/RiskStratificationDialog';
import RmsGaugeChart from '../../components/provider/RmsGaugeChart';
import { formatDateShort } from '../../../utils/timestampUtils';
import RmsFormDetailsDialog from '../../components/provider/RmsFormDetailsDialog';
import RmsEditGuardianDialog from '../../components/provider/RmsEditGuardianDialog';
import AssignTriageFormsDialog from '../../components/AssignTriageFormsDialog';
import RmsTopNavBar from '../../components/provider/RmsTopNavBar';
import HfpV2SummaryTable from '../../components/hfp/HfpV2SummaryTable';
import HfpV3OverviewMatrix from '../../components/hfp/HfpV3OverviewMatrix';
import HfpCardDisclaimer from '../../components/hfp/HfpCardDisclaimer';
import { getReferralReasonAgreement } from '../../utils/referralReasonAgreement';
import ConcernBadge from '../../components/ConcernBadge';
import { apiGetReferralParticipants, apiMarkReadyForTriage, apiStartTriage, apiUnassignTriage } from '../../utils/rmsApi';
import ConsentWithdrawnDialog from '../../components/provider/ConsentWithdrawnDialog';
import ConsentWithdrawnBanner from '../../components/provider/ConsentWithdrawnBanner';
import RecordConsentWithdrawalDialog from '../../components/provider/RecordConsentWithdrawalDialog';
import useConsentWithdrawnStop from '../../hooks/useConsentWithdrawnStop';
import { isNonClinicalProvider } from '../../utils/providerRoles';
import { toast } from 'react-toastify';
import { canTriage, canOpenTriageScreens, canMarkReadyForTriage } from '../../../constants/userTypes';
import { canAccessConcern, getIntakeConcernLabel } from '../../utils/specialismMatch';
import {
  STATUS,
  BASE_REFERRAL_FORMS,
  HFP_FORM_KEYS,
  HFP_V1_FORM_KEYS,
  HFP_V3_FORM_KEYS,
  hfpKeysForRoleData,
  hfpFormsForRoleData,
  TRIAGE_FORM_KEYS,
  isTriageFormInstanceKey,
  calculateRoleStatus,
  getStatusBadgeConfig,
  effectiveBaseReferralForms,
  completionPercent,
  isRoleReferralFormsComplete,
  areAllTriageFormsComplete,
  getTriageDecisionSummary,
  getPathwayTriageStatusLabel,
  inReviewLabel,
  resolveRoleFormInstance,
  hfpInstrumentForRecord,
  hasStoredHfpInstrument,
  classifyReferralStage,
} from '../../utils/referralStatus';
import {
  profileSharedWithClinicians,
  profileAwaitingCompletion,
  PROFILE_NOT_SHARED_LINE,
} from '../../utils/profileSharing';
import ServiceUserUploadsPanel from '../../components/provider/ServiceUserUploadsPanel';
import UrgentRiskAlertFlag from '../../components/provider/UrgentRiskAlertFlag';
import ChangeDecisionButton from '../../components/provider/ChangeDecisionButton';
import {
  DECISION_CHANGEABLE_NOTICE,
  FIRST_REJECTION_CHANGEABLE_NOTICE,
  triageDecisionChange,
} from '../../utils/triageDecisionChange';
import {
  isCombinedConcern,
  pathwaysForConcern,
  pathwayReviews,
  pathwayTriage,
  resolveActingPathway,
  PATHWAY_LABELS,
} from '../../utils/triagePathways';
import {
  TIER_CONFIG,
  tierToRiskLevel,
} from '../../utils/riskStratification';
import { canSeeFirstReviewerName, WITHHELD_REVIEWER_NAME } from '../../utils/decisionAttribution';
import { isExportableFormKey, downloadableTriageDecisions } from '../../utils/referralDocumentExport';
import useReferralDocumentDownload from '../../hooks/useReferralDocumentDownload';
import { PARTICIPANT_STATUS } from '../../../constants/participantStatuses';
import { isRiskStratificationVisible } from '../../constants/orgFeatures';

// Environment check for showing dev tools (auto-complete buttons)
const isDev = isDevAffordanceEnabled();

// Badge classes for the per-pathway triage status flags (TD-129 AC3).
const PATHWAY_STATUS_TONE_CLASSES = {
  green: 'bg-green-50 text-green-700 border border-green-200',
  rose: 'bg-rose-50 text-rose-700 border border-rose-200',
  amber: 'bg-amber-50 text-amber-700 border border-amber-200',
  primary: 'bg-primary/10 text-primary',
  gray: 'bg-gray-100 text-gray-600 border border-gray-200',
};

/**
 * RmsPatientOverview
 * ------------------
 * Purpose:
 *   Provider-facing detailed view of a single CYP patient record.
 *   Shows form progress for both School User and Legal Guardian,
 *   plus the Risk Stratification panel when triage is complete.
 *
 * Layout:
 *   - Header: Patient info (name, DOB, referral date, referrer type)
 *   - Two-column layout: School User forms | Legal Guardian forms
 *   - Risk Stratification panel (conditionally visible)
 *
 * Data:
 *   - Patient record from rms_organisations/{orgId}/patientRecords/{id}
 *   - Form instances from formInstances subcollection
 */

// Form configuration - defines which forms each role sees. HFP forms are NOT
// listed here: the record can hold any of three HFP instrument generations
// (v3/v2/v1), so the HFP rows are derived per record + role via
// referralStatus.hfpFormsForRoleData — the same key-set detection the
// completion counters use. A static list here drifts: a v1 list showed
// "12 of 12" vs "4 of 11" (TD-136), and a static v2 list showed every HFP
// form as empty/0% on v3-only records while the counters read them complete.
/**
 * The strengths and needs (HFP) keys that count for a role on this record.
 * They are the SEND pathway's forms, never the referral's (Tom King, 27
 * October 2026; TDI-495, TDI-511): counted while the record's SEND pathway
 * is active, or when the role already holds answers on them (records that
 * collected them before the pathways were separated), and never for a
 * secondary-school pupil, who gets no profile. Otherwise an empty list, so a
 * referral-only child is judged on the referral forms alone, as the server
 * judges it.
 *
 * They also drop out entirely until the family has shared the profile (Eve
 * Crooijmans, 30 October 2026; TDI-483): a clinician is not told that the
 * school still owes seven strengths and needs forms on a profile the family
 * has not let the clinical team see. This is the same flag the subscription
 * reads, so the count can never ask for forms that were never fetched — which
 * is what left a SEND-active record showing a "Completed" chip beside
 * "4/11 forms" on a referral where every readable form was done.
 */
function hfpKeysCountedForRole(role, progressByKey, patientRecord) {
  const phase = patientRecord?.schoolPhase || patientRecord?.school?.schoolType;
  if (phase === 'secondary') return [];
  if (!profileSharedForRules(patientRecord)) return [];
  const keys = hfpKeysForRoleData(role, progressByKey, patientRecord);
  const senActive = patientRecord?.senPathway?.status === 'active';
  const holdsAnswers = keys.some((k) => Object.prototype.hasOwnProperty.call(progressByKey || {}, `${k}_${role}`));
  return senActive || holdsAnswers ? keys : [];
}

const FORM_GROUPS = {
  referral: {
    title: 'Referral Forms',
    forms: [
      { key: 'requestForReferral', title: 'Request for Referral' },
      { key: 'reasonForReferral', title: 'Reason for Referral' },
      { key: 'educationHistory', title: 'Education History' },
      { key: 'snapIV26', title: 'SNAP-IV 26' },
    ],
  },
  triage: {
    title: 'Triage Forms',
    forms: [
      { key: 'theirEarlyLife', title: 'Their Early Life' },
      { key: 'theirFamilyAndHomeLife', title: 'Their Family & Home Life' },
      { key: 'theirLifestyle', title: 'Their Lifestyle' },
      { key: 'theirHistoryWithTheLaw', title: 'Their History with the Law' },
      { key: 'theirFamilyHistory', title: 'Their Family History' },
      { key: 'theirMedicalHistory', title: 'Their Medical History' },
      { key: 'theirMentalHealthHistory', title: 'Their Mental Health History' },
    ],
  },
};

/**
 * AutoCompleteButton
 * ------------------
 * Dev-only button to auto-complete a form with demo data.
 * Only visible in local development only.
 * Can be re-clicked even on completed forms to trigger recalculation (e.g., risk stratification).
 */
const AutoCompleteButton = ({ formKey, role, orgId, patientRecordId, onComplete, isComplete }) => {
  const [loading, setLoading] = useState(false);

  const handleAutoComplete = async (e) => {
    e.stopPropagation(); // Prevent any parent click handlers
    if (loading) return;

    setLoading(true);
    try {
      const autoCompleteForm = httpsCallable(functions, 'rmsAutoCompleteForm');
      const result = await autoCompleteForm({
        orgId,
        patientRecordId,
        formKey,
        role,
      });

      if (result.data?.success) {
        console.log(`[AutoComplete] Successfully completed ${formKey} for ${role}:`, result.data);
        // Log if risk was calculated
        if (result.data.riskCalculated) {
          console.log(`[AutoComplete] Risk stratification calculated! Tier: ${result.data.riskTier}`);
        }
        if (onComplete) onComplete(formKey, role, result.data);
      } else {
        console.error('[AutoComplete] Failed:', result.data);
      }
    } catch (err) {
      console.error('[AutoComplete] Error:', err);
      // Show error but don't block - form data is auto-refreshed via snapshot
    } finally {
      setLoading(false);
    }
  };

  if (!isDev) return null;

  return (
    <button
      type="button"
      onClick={handleAutoComplete}
      disabled={loading}
      title={isComplete ? 'Re-run auto-complete (triggers risk calculation)' : 'Auto-complete with demo data'}
      className={`p-1 rounded transition-colors ${
        loading
          ? 'text-amber-500 animate-pulse'
          : isComplete
          ? 'text-green-500 hover:text-green-700 hover:bg-green-50'
          : 'text-primary hover:text-primary/80 hover:bg-primary/5'
      }`}
    >
      <LightningBoltIcon className="w-4 h-4" />
    </button>
  );
};

// /**
//  * SemiCircleProgress
//  * ------------------
//  * A semi-circular progress indicator used to display percentage scores.
//  * Used in the SNAP-IV 26 Score Panel to visualize ADHD symptom scores.
//  */
// const SemiCircleProgress = ({
//   percentage = 0,
//   size = 120,
//   strokeWidth = 18,
//   color = "#43213e",
//   bgColor = "#e5e7eb",
//   rounded = true,
//   textColor = "#43213e",
//   className = "",
// }) => {
//   // Clamp percentage to 0-100 range
//   const pct = Math.max(0, Math.min(percentage, 100));

//   // Calculate radius and adjusted radius so stroke fits inside viewbox
//   const radius = size / 2;
//   const normalizedRadius = radius - strokeWidth / 2;

//   // Arc length for a half-circle: PI * r
//   const arcLength = Math.PI * normalizedRadius;

//   // Stroke dash controls for progress animation
//   const strokeDasharray = `${arcLength} ${arcLength}`;
//   const strokeDashoffset = arcLength * (1 - pct / 100);

//   // Path start and end (left -> right across the top half of the circle)
//   const startX = strokeWidth / 2;
//   const startY = radius;
//   const endX = size - strokeWidth / 2;
//   const endY = radius;
//   const arcPath = `M ${startX} ${startY} A ${normalizedRadius} ${normalizedRadius} 0 0 1 ${endX} ${endY}`;

//   // Choose cap style - round looks nicer for progress indicators
//   const strokeLinecap = rounded ? "round" : "butt";

//   return (
//     <div
//       className={`relative inline-flex items-end justify-center ${className}`}
//       style={{ width: size, height: radius + strokeWidth / 2 }}
//       aria-label={`Progress ${pct}%`}
//     >
//       <svg
//         width={size}
//         height={radius + strokeWidth / 2}
//         viewBox={`0 0 ${size} ${radius + strokeWidth / 2}`}
//         className="overflow-visible"
//       >
//         {/* Background semicircle */}
//         <path
//           d={arcPath}
//           fill="none"
//           stroke={bgColor}
//           strokeWidth={strokeWidth}
//           strokeLinecap={strokeLinecap}
//         />

//         {/* Foreground (progress) semicircle */}
//         <path
//           d={arcPath}
//           fill="none"
//           stroke={color}
//           strokeWidth={strokeWidth}
//           strokeLinecap={strokeLinecap}
//           strokeDasharray={strokeDasharray}
//           strokeDashoffset={strokeDashoffset}
//           style={{ transition: "stroke-dashoffset 800ms ease" }}
//         />
//       </svg>

//       {/* Percentage text */}
//       <div
//         className="absolute flex items-center justify-center"
//         style={{ top: "46%", transform: "translateY(0%)" }}
//       >
//         <span
//           className="text-xl font-bold"
//           style={{ color: textColor }}
//         >
//           {Math.round(pct)}%
//         </span>
//       </div>
//     </div>
//   );
// };

/**
 * ADHDSymptomScoreCard
 * --------------------
 * Displays ADHD Symptom Score (based on SNAP-IV 26 results) in an OverviewCard.
 * Always visible, but shows a locked/greyed state when both guardian and school
 * have NOT completed the SNAP-IV. When both are complete, displays the full
 * score panel with a toggle to switch between data sources.
 * 
 * Props:
 *   formInstances: Object containing all form instances with scaleResults
 */
// const ADHDSymptomScoreCard = ({ formInstances }) => {
//   const [dataSource, setDataSource] = useState('guardian');
  
//   // Extract scale results from both guardian and school form instances
//   const guardianInstance = formInstances['snapIV26_guardian'] || {};
//   const schoolInstance = formInstances['snapIV26_school_user'] || {};
  
//   const guardianResults = guardianInstance.scaleResults;
//   const schoolResults = schoolInstance.scaleResults;
  
//   // Check completion status for SNAP-IV forms
//   const guardianComplete = guardianResults?.isComplete;
//   const schoolComplete = schoolResults?.isComplete;
//   const bothComplete = guardianComplete && schoolComplete;
  
//   // Show locked state if BOTH SNAP-IV forms are not complete
//   if (!bothComplete) {
//     // Build a helpful message showing which forms are still pending
//     let pendingMessage = 'Awaiting SNAP-IV completion from ';
//     if (!guardianComplete && !schoolComplete) {
//       pendingMessage += 'Parent/Guardian and School';
//     } else if (!guardianComplete) {
//       pendingMessage += 'Parent/Guardian';
//     } else {
//       pendingMessage += 'School';
//     }
    
//     return (
//       <OverviewCard 
//         title="ADHD Symptom Score" 
//         isLocked 
//         lockedMessage={pendingMessage}
//       />
//     );
//   }
  
//   // Get current data source results
//   const currentResults = dataSource === 'guardian' ? guardianResults : schoolResults;
//   const percentage = Math.round((currentResults.totalScore / currentResults.maxTotalScore) * 100);
  
//   // Determine if threshold is crossed (any subscale above clinical threshold)
//   const thresholdCrossed = 
//     currentResults.inattention?.aboveThreshold || 
//     currentResults.hyperactivityImpulsivity?.aboveThreshold ||
//     currentResults.oppositionDefiance?.aboveThreshold;

//   // When both SNAP-IV forms are complete, show full score display
//   return (
//     <OverviewCard 
//       title="ADHD Symptom Score"
//       headerRight={
//         // Data source toggle in the header
//         <div className="flex items-center gap-2 text-xs">
//           <span className="text-gray-500">Data source:</span>
//           <div className="flex rounded-lg border border-gray-200 overflow-hidden">
//             <button
//               type="button"
//               onClick={() => setDataSource('guardian')}
//               className={`px-3 py-1.5 transition-colors ${
//                 dataSource === 'guardian'
//                   ? 'bg-primary/10 text-primary font-semibold'
//                   : 'bg-white text-gray-600 hover:bg-gray-50'
//               }`}
//             >
//               Parent
//             </button>
//             <button
//               type="button"
//               onClick={() => setDataSource('school')}
//               className={`px-3 py-1.5 transition-colors ${
//                 dataSource === 'school'
//                   ? 'bg-primary/10 text-primary font-semibold'
//                   : 'bg-white text-gray-600 hover:bg-gray-50'
//               }`}
//             >
//               School
//             </button>
//           </div>
//         </div>
//       }
//     >
//       {/* Score display */}
//       <div className="flex items-center gap-6">
//         {/* Semi-circle progress bar */}
//         <div className="flex-shrink-0">
//           <SemiCircleProgress 
//             percentage={percentage}
//             color={thresholdCrossed ? "#43213e" : "#ced6f6"}
//           />
//         </div>
        
//         {/* Text content */}
//         <div className="flex-1">
//           <div className="text-sm text-gray-600 mb-2">
//             Score of <span className="font-bold text-primary">{currentResults.totalScore}/{currentResults.maxTotalScore}</span> on the SNAP-IV
//           </div>
//           <div className={`text-sm font-semibold ${thresholdCrossed ? 'text-primary' : 'text-gray-500'}`}>
//             {thresholdCrossed ? 'Threshold crossed' : 'Threshold not met'}
//           </div>
          
//           {/* Subscale breakdown */}
//           <div className="mt-3 space-y-1 text-xs text-gray-500">
//             <div className="flex items-center gap-2">
//               <span className={currentResults.inattention?.aboveThreshold ? 'text-amber-600 font-semibold' : ''}>
//                 Inattention: {currentResults.inattention?.score}/{currentResults.inattention?.maxScore}
//               </span>
//               {currentResults.inattention?.aboveThreshold && (
//                 <span className="px-1.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
//                   {currentResults.inattention?.severity}
//                 </span>
//               )}
//             </div>
//             <div className="flex items-center gap-2">
//               <span className={currentResults.hyperactivityImpulsivity?.aboveThreshold ? 'text-amber-600 font-semibold' : ''}>
//                 Hyperactivity: {currentResults.hyperactivityImpulsivity?.score}/{currentResults.hyperactivityImpulsivity?.maxScore}
//               </span>
//               {currentResults.hyperactivityImpulsivity?.aboveThreshold && (
//                 <span className="px-1.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
//                   {currentResults.hyperactivityImpulsivity?.severity}
//                 </span>
//               )}
//             </div>
//             <div className="flex items-center gap-2">
//               <span className={currentResults.oppositionDefiance?.aboveThreshold ? 'text-amber-600 font-semibold' : ''}>
//                 Opposition: {currentResults.oppositionDefiance?.score}/{currentResults.oppositionDefiance?.maxScore}
//               </span>
//               {currentResults.oppositionDefiance?.aboveThreshold && (
//                 <span className="px-1.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
//                   {currentResults.oppositionDefiance?.severity}
//                 </span>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>
//     </OverviewCard>
//   );
// };


// Get patient name from record
const getPatientName = (record) => {
  const firstName = record?.subject?.firstName || record?.subject_first_name || record?.firstName || '';
  const lastName = record?.subject?.lastName || record?.subject_last_name || record?.lastName || '';
  return [firstName, lastName].filter(Boolean).join(' ').trim() || 'Unknown Patient';
};

// ─────────────────────────────────────────────────────────────────────────────
// New Card-Based Layout Components
// ─────────────────────────────────────────────────────────────────────────────

/**
 * OverviewCard - Base card component for the overview grid
 * Supports locked state with centered lock icon and message (matching design)
 * When locked, both header and content have gray-100 background for consistency.
 * Headers are always centered for visual consistency across the grid.
 */
const OverviewCard = ({ 
  title, 
  children, 
  isLocked = false, 
  lockedMessage = 'Not available',
  hasLeftBorder = false,
  headerRight = null,
  className = ''
}) => {
  return (
    <div 
      className={`
        bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden
        flex flex-col
        ${hasLeftBorder ? 'border-l-4 border-l-primary' : ''}
        ${className}
      `}
    >
      {/* Card header - always centered, gray background when locked */}
      <div className={`px-5 py-4 border-b border-gray-100 text-center ${isLocked ? 'bg-gray-100' : ''}`}>
        <h3 className={`text-lg font-bold ${isLocked ? 'text-gray-500' : 'text-gray-900'}`}>{title}</h3>
        {!isLocked && headerRight && (
          <div className="mt-1">{headerRight}</div>
        )}
      </div>
      
      {/* Card content */}
      <div className="relative flex-1">
        {isLocked ? (
          /* Locked state: centered lock icon and message matching the design */
          <div className="h-full min-h-[180px] bg-gray-100 flex flex-col items-center justify-center px-5 py-8">
            {/* Large centered lock icon */}
            <LockClosedIcon className="w-16 h-16 text-gray-300 mb-4" />
            {/* Centered message text - supports line breaks */}
            <p className="text-sm font-medium text-gray-400 text-center whitespace-pre-line leading-relaxed">
              {lockedMessage}
            </p>
          </div>
        ) : (
          <div className="px-5 py-4">
            {children}
          </div>
        )}
      </div>
    </div>
  );
};


/**
 * HfpV2Card - Holistic Functional Profile (v2) clinical summary square.
 * Shows the per-domain / per-subdomain Home vs School RAG table from the v2
 * scoring engine, plus a button to open the full HFP profile page.
 * The legacy v1 card (radar/spider charts) has been retired.
 */
/**
 * TDI-483: the profile card when the family has not shared the profile with
 * the clinical team (no yes, or SEND consent withdrawn). One
 * neutral line, no scores, no link: nothing here implies what the profile
 * says or that the clinician is owed it.
 */
const HfpNotSharedCard = ({ className = '' }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`} role="status">
    <h3 className="text-base font-semibold text-gray-900">Strengths and needs profile</h3>
    <p className="text-sm text-gray-600 mt-2">{PROFILE_NOT_SHARED_LINE}</p>
  </div>
);

/**
 * TDI-526: the profile card for a v4 record the family has shared. The v4
 * clinician view is a fixed, levels-only page with no AI-written text (D78),
 * so the card only says what it holds and links to it; nothing of the
 * profile is summarised here.
 */
const HfpV4Card = ({ onViewProfile, className = '' }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col gap-3 ${className}`}>
    <h3 className="text-base font-semibold text-gray-900">Strengths and needs profile</h3>
    <p className="text-sm text-gray-600">
      Levels at home and at school for each area, from the parent&apos;s and the teacher&apos;s answers, with the answers behind them.
    </p>
    <div>
      <button
        type="button"
        onClick={onViewProfile}
        className="min-h-[44px] inline-flex items-center rounded-lg border border-primary px-4 py-2 text-sm font-medium text-primary hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
      >
        Clinician view
      </button>
    </div>
  </div>
);

const HfpV2Card = ({ scoring, onViewProfile, className = '', isSecondary = false }) => {
  const hasScoring = !!scoring && !isSecondary;

  // Locked states use the shared OverviewCard lock (large grey block + centred
  // lock icon + reason), so the unavailable HFP looks identical to the Rating
  // Scales card. Secondary-school referrals skip the HFP (TD-129/TD-137);
  // otherwise it is pending both respondents completing their HFP forms.
  if (!hasScoring) {
    return (
      <OverviewCard
        title="Holistic Functional Profile"
        className={className}
        isLocked
        lockedMessage={
          isSecondary
            ? 'Not available for\nsecondary-school CYP'
            : 'The profile will appear here once both\nrespondents have completed all HFP forms'
        }
      />
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex flex-col ${className}`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-base font-semibold text-gray-900 min-w-0">Holistic Functional Profile</h3>
        <button
          type="button"
          onClick={onViewProfile}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90"
        >
          Service User View
        </button>
      </div>
      <div className="flex-1 overflow-y-auto -mx-1 px-1">
        <HfpV2SummaryTable scoring={scoring} />
      </div>
      <HfpCardDisclaimer />
    </div>
  );
};

/**
 * HfpV3Card - Holistic Functional Profile (v3) home-vs-school matrix
 * (build instruction section 5.1; TD-208). Rendered when the record carries a
 * v3 scoring payload; records scored under v2 keep the HfpV2Card unchanged.
 */
const HfpV3Card = ({ scoring, onViewProfile, onOpenArea, className = '' }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex flex-col ${className}`}>
    <div className="flex items-center justify-between gap-3 mb-3">
      <h3 className="text-base font-semibold text-gray-900 min-w-0">Holistic Functional Profile</h3>
      <button
        type="button"
        onClick={onViewProfile}
        className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90"
      >
        Service User View
      </button>
    </div>
    <div className="flex-1 overflow-y-auto -mx-1 px-1">
      <HfpV3OverviewMatrix scoring={scoring} onOpenArea={onOpenArea} />
    </div>
    <p className="mt-2 text-[11px] text-gray-400">
      Ratings use thresholds that are under clinical calibration.
    </p>
    <HfpCardDisclaimer />
  </div>
);


/**
 * NeurodevelopmentalScalesCard
 * ----------------------------
 * Displays neurodevelopmental rating scale results (currently SNAP-IV for ADHD).
 *
 * Design:
 *   - Each scale is rendered as a row styled consistently with PatientScalesPanel:
 *     scale name | progress bar | score | threshold badge | magnifying glass icon
 *   - A toggle in the card header lets the clinician switch between Parent/Carer
 *     and School respondents when both have completed the scale.
 *   - Clicking the magnifying glass icon opens the existing RmsFormDetailsDialog
 *     for the SNAP-IV form instance belonging to the currently selected respondent,
 *     showing the full set of submitted responses.
 *
 * The card shows a locked state when one or both respondents haven't completed.
 *
 * Props:
 *   formInstances  — Object containing all form instances keyed by instance ID
 *   onViewDetails  — callback(formKey, formTitle, instanceId, formInstance) to open the details dialog
 */
const NeurodevelopmentalScalesCard = ({ formInstances, onViewDetails, isAsdOnly = false }) => {
  // Which respondent's data to display (default: parent/guardian).
  const [respondent, setRespondent] = useState('guardian');

  // Autism-only referrals don't collect the SNAP-IV (an ADHD rating scale, TD-139)
  // and there is no autism rating scale, so the card is locked. (Hook above runs
  // unconditionally so the early return doesn't change hook order.)
  if (isAsdOnly) {
    return (
      <OverviewCard
        title="Rating Scales"
        isLocked
        lockedMessage={"Not applicable for an\nautism-only referral"}
      />
    );
  }

  // ── Extract SNAP-IV results from both respondents ─────────────────────────
  const guardianInstance = formInstances['snapIV26_guardian'] || {};
  const schoolInstance = formInstances['snapIV26_school_user'] || {};

  const guardianResults = guardianInstance.scaleResults;
  const schoolResults = schoolInstance.scaleResults;

  const guardianComplete = Boolean(guardianResults?.isComplete);
  const schoolComplete = Boolean(schoolResults?.isComplete);
  const bothComplete = guardianComplete && schoolComplete;

  // Build a descriptive locked message when not all respondents have completed.
  let lockedMessage = 'Awaiting completion of forms by ';
  if (!guardianComplete && !schoolComplete) {
    lockedMessage += 'Parent/Carer & School';
  } else if (!guardianComplete) {
    lockedMessage += 'Parent/Carer';
  } else {
    lockedMessage += 'School';
  }

  // If neither respondent has completed, show a locked card.
  if (!guardianComplete && !schoolComplete) {
    return (
      <OverviewCard
        title="Rating Scales"
        isLocked
        lockedMessage={lockedMessage}
      />
    );
  }

  // Determine which results to show based on the active toggle.
  // If the selected respondent hasn't completed yet, fall back to whoever has.
  const activeResults =
    respondent === 'guardian'
      ? (guardianComplete ? guardianResults : schoolResults)
      : (schoolComplete ? schoolResults : guardianResults);

  // ── Build scale items from the active respondent's data ───────────────────
  const percentage = activeResults?.maxTotalScore > 0
    ? Math.round((activeResults.totalScore / activeResults.maxTotalScore) * 100)
    : 0;

  const thresholdCrossed =
    activeResults?.inattention?.aboveThreshold ||
    activeResults?.hyperactivityImpulsivity?.aboveThreshold ||
    activeResults?.oppositionDefiance?.aboveThreshold;

  const scaleItems = [
    {
      id: 'adhd',
      label: 'ADHD (SNAP-IV)',
      scaleName: 'SNAP-IV',
      score: activeResults?.totalScore ?? 0,
      maxScore: activeResults?.maxTotalScore ?? 0,
      thresholdMet: Boolean(thresholdCrossed),
      percentage,
      scaleResults: activeResults,
    },
    // Additional scales can be added here when data sources are available:
    // { id: 'dyspraxia', label: 'Dyspraxia (DCD-Q)', scaleName: 'DCD-Q', ... },
  ];

  // ── Respondent toggle (Parent / School) ───────────────────────────────────
  // Only shown when both respondents have completed; otherwise the toggle is hidden
  // and the available respondent's data is shown directly.
  const respondentToggle = bothComplete ? (
    <div className="inline-flex bg-gray-100 rounded-lg p-0.5 text-xs font-medium">
      <button
        type="button"
        onClick={() => setRespondent('guardian')}
        className={`px-3 py-1 rounded-md transition-colors ${
          respondent === 'guardian'
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        Parent / Carer
      </button>
      <button
        type="button"
        onClick={() => setRespondent('school')}
        className={`px-3 py-1 rounded-md transition-colors ${
          respondent === 'school'
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        School
      </button>
    </div>
  ) : null;

  return (
    <>
      <OverviewCard title="Rating Scales" headerRight={respondentToggle}>
        <div className="space-y-2">
          {scaleItems.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
            >
              {/* Green checkmark — completed indicator */}
              {/* <div className="flex items-center justify-center h-5 w-5 rounded-full bg-green-100 flex-shrink-0">
                <CheckCircleIcon className="h-3 w-3 text-green-600" />
              </div> */}

              {/* Scale name + threshold status (italic text below) */}
              <div className="flex-shrink-0 text-left" style={{ flexBasis: '140px' }}>
                <h4 className="text-sm font-medium text-gray-900 leading-tight">{item.label}</h4>
                <p className={`text-xs font-bold mt-0.5 ${item.thresholdMet ? 'text-primary' : 'text-gray-400'}`}>
                  {item.thresholdMet ? 'Threshold crossed' : 'Threshold not met'}
                </p>
              </div>

              {/* Progress bar — always brand primary */}
              <div className="w-20 flex-shrink-0">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-2 rounded-full bg-primary transition-all duration-300"
                    style={{ width: `${item.percentage}%` }}
                  />
                </div>
              </div>

              {/* Score + magnifying glass icon (grouped together) */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <span className="text-sm font-medium text-gray-600 whitespace-nowrap">
                  {item.score}/{item.maxScore}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    // Open the existing RmsFormDetailsDialog for the SNAP-IV form
                    // belonging to the currently selected respondent.
                    // The first arg (formKey) must be the base schema key ('snapIV26'),
                    // not the instance ID, because the dialog uses it to fetch the schema.
                    const snapRole = respondent === 'guardian' ? 'guardian' : 'school_user';
                    const instanceId = `snapIV26_${snapRole}`;
                    const instance = formInstances[instanceId] || {};
                    if (onViewDetails) {
                      onViewDetails('snapIV26', 'SNAP-IV 26', instanceId, instance, snapRole);
                    }
                  }}
                  className="flex items-center justify-center w-6 h-6 text-primary/70 rounded-full hover:bg-primary/10 transition-colors"
                  title="View form responses"
                >
                  <SearchIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </OverviewCard>
    </>
  );
};


/**
 * ReferralDetailsCard
 * -------------------
 * At-a-glance referral metadata for the clinician overview: what the referral is
 * for, when it was started and received, who referred it, and the nominated
 * parent/carer.
 */
const ReferralDetailsCard = ({
  referredForLabel,
  referralStarted,
  // The date the referral entered the triage queue (all forms completed, or a
  // clinician marked it ready). Null until then — the row is omitted, not "—",
  // so an in-progress referral never implies it has been received.
  referralReceived,
  referrerName,
  referrerRole,
  guardianName,
  guardianRelationship,
  // LSC-016: per-side referral-reason endorsement + agreement signal, from
  // getReferralReasonAgreement. Null-safe — older records may lack answers.
  reasonAgreement,
}) => (
  <OverviewCard title="Referral Details">
    <div className="py-2 space-y-2 text-sm text-left text-gray-700">
      <p>
        <span className="font-semibold text-gray-900">Referred for:</span>{' '}
        {referredForLabel || 'Not yet classified'}
      </p>
      {reasonAgreement && (reasonAgreement.school || reasonAgreement.guardian) && (
        <div>
          <p className="font-semibold text-gray-900">Referral reason by respondent:</p>
          <div className="mt-1 space-y-1">
            <p>
              School:{' '}
              {reasonAgreement.school
                ? `${reasonAgreement.school.label}${reasonAgreement.school.detail ? `: ${reasonAgreement.school.detail}` : ''}`
                : 'Not answered yet'}
            </p>
            <p>
              Parent/Carer:{' '}
              {reasonAgreement.guardian
                ? `${reasonAgreement.guardian.label}${reasonAgreement.guardian.detail ? `: ${reasonAgreement.guardian.detail}` : ''}`
                : 'Not answered yet'}
            </p>
          </div>
          {/* Agreement signal (24/07/2026 decision): green when the school and
              parent name the same reason, amber when they differ, and no
              indicator while either side has not answered. Colour never rides
              alone — each state carries an icon and a text label. */}
          {reasonAgreement.status === 'agree' && (
            <p className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
              <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
              School and parent agree
            </p>
          )}
          {/* On a disagreement, split the signal per pathway where derivable
              (the agreed view): school "both" vs parent "ADHD" agrees on ADHD
              and differs on autism. Falls back to a single mismatch chip when
              either side chose "other". */}
          {reasonAgreement.status === 'differ' && (
            reasonAgreement.pathways ? (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {reasonAgreement.pathways.map(({ pathway, label, status }) => (
                  status === 'agree' ? (
                    <span
                      key={pathway}
                      className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700"
                    >
                      <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      {label}: school and parent agree
                    </span>
                  ) : (
                    <span
                      key={pathway}
                      className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700"
                    >
                      <ExclamationIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      {label}: school and parent differ
                    </span>
                  )
                ))}
              </div>
            ) : (
              <p className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                <ExclamationIcon className="h-3.5 w-3.5" aria-hidden="true" />
                School and parent differ
              </p>
            )
          )}
        </div>
      )}
      <p>
        <span className="font-semibold text-gray-900">Referral started:</span>{' '}
        {referralStarted || '—'}
      </p>
      {referralReceived && (
        <p>
          <span className="font-semibold text-gray-900">Referral received:</span>{' '}
          {referralReceived}
        </p>
      )}
      <p>
        <span className="font-semibold text-gray-900">Referred by:</span>{' '}
        {referrerName
          ? `${referrerName}${referrerRole ? `, ${referrerRole}` : ''}`
          : '—'}
      </p>
      {guardianName && (
        <p>
          <span className="font-semibold text-gray-900">Parent/Carer:</span>{' '}
          {guardianName}
          {guardianRelationship ? `, ${guardianRelationship}` : ''}
        </p>
      )}
    </div>
  </OverviewCard>
);


// ─────────────────────────────────────────────────────────────────────────────
// Risk Stratification Card - Uses shared TIER_CONFIG from riskStratification.js
// ─────────────────────────────────────────────────────────────────────────────
// TIER_CONFIG and tierToRiskLevel are imported from '../../utils/riskStratification'
// to ensure consistent colors across the application (e.g., orange for High Priority).

/**
 * RiskStratificationCard
 * ----------------------
 * Displays the risk stratification gauge and "View Risks" CTA.
 * Shows locked state when triage questionnaire is not complete.
 * 
 * This replaces the previous pattern of wrapping RiskStratificationPanel
 * inside OverviewCard, which caused double-nesting of card styles.
 * 
 * Props:
 *   riskData: The riskStratification object from the patient record
 *   orgId: Organisation ID for fetching schemas in detail view
 *   patientRecordId: Patient record ID for manual review assignment
 *   respondentNames: { guardian: { firstName, lastName, relationshipLabel }, school_user: { firstName, lastName, role } }
 */
const RiskStratificationCard = ({ riskData, orgId, patientRecordId, respondentNames, canReview = false, hfpPainOverride = null }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  // Show locked state when risk data is not available
  // With continuous risk calculation, this only shows before any risk-relevant
  // form is submitted. Once any form with risk indicators is submitted, risk
  // calculation runs and we show the gauge (potentially with partial data banner).
  if (!riskData?.success) {
    return (
      <OverviewCard 
        title="Suggested Priority Level" 
        isLocked 
        lockedMessage={"Awaiting data from forms"}
      />
    );
  }
  
  // Extract risk tier and config (using shared TIER_CONFIG for consistent colors)
  const tier = riskData.tier || 4;
  const config = TIER_CONFIG[tier] || TIER_CONFIG[4];
  
  // Calculate elevated indicator counts and highest risk level
  // Elevated indicators have score > 1 (Medium=2, High=3, Urgent=4)
  // We show the count of only the highest level (not the total of all elevated)
  const indicatorStats = (() => {
    const domainSummaries = riskData.domainSummaries || {};
    let urgentCount = 0;
    let highCount = 0;
    let mediumCount = 0;
    
    Object.values(domainSummaries).forEach((summary) => {
      urgentCount += summary.urgentCount || 0;
      highCount += summary.severeCount || 0;
      mediumCount += summary.moderateCount || 0;
    });
    
    // Determine highest risk level and its count
    // Only show the count for the highest level present
    let highestLevel = null;
    let highestLevelCount = 0;
    
    if (urgentCount > 0) {
      highestLevel = 'Urgent';
      highestLevelCount = urgentCount;
    } else if (highCount > 0) {
      highestLevel = 'High';
      highestLevelCount = highCount;
    } else if (mediumCount > 0) {
      highestLevel = 'Medium';
      highestLevelCount = mediumCount;
    }
    
    return { highestLevel, highestLevelCount };
  })();
  
  // Get manual review counts from risk data
  const manualReviewCounts = riskData.manualReviewCounts || { guardian: 0, school_user: 0 };
  const totalManualReview = (manualReviewCounts.guardian || 0) + (manualReviewCounts.school_user || 0);
  
  return (
    <>
      <OverviewCard title="Suggested Priority Level">
        <div className="flex flex-col items-center">
          {/* Gauge Chart - centered, sized to fill panel width */}
          <div className="mb-3 w-full flex justify-center">
            <RmsGaugeChart
              riskLevel={tierToRiskLevel(tier)}
              size={200}
              thickness={45}
              segmentColor={config.segmentColor}
              captionText={config.label}
              captionColor={config.captionColor}
            />
          </div>
          
          {/* Partial data warning banner - shown when not all forms are submitted yet
              This indicates that the priority level is based on incomplete data and may
              change as more forms are completed. The banner disappears when all expected
              triage and referral forms have been submitted. */}
          {riskData.isPartialData && (
            <div className="mb-3 w-full px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <ExclamationIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">
                This priority level is based on partial data and may change as more forms are completed.
              </p>
            </div>
          )}
          
          {/* Dynamic bullet points summarising risk indicators and manual review items */}
          <ul className="text-xs text-gray-600 mb-3 space-y-1.5 w-42">
            {/* HFP v3 pain safety override (instruction section 8 [build]): a
                distinct, non-colour marker whenever the sensory profile shows
                reduced pain awareness. Absent for v2 records. */}
            {hfpPainOverride && (
              <li className="flex items-center gap-1.5">
                <span aria-hidden="true">⚠</span>
                <span className="font-semibold">
                  Reduced pain awareness (HFP sensory profile{hfpPainOverride.contexts}) — pain safety override applied
                </span>
              </li>
            )}
            {/* Elevated risk indicators summary - shows count of highest level only */}
            {indicatorStats.highestLevelCount > 0 ? (
              <li className="flex items-center gap-1.5">
                <span className="text-gray-400">•</span>
                <span>
                  <span className="font-semibold">{indicatorStats.highestLevelCount}</span>
                  {' '}
                  <span
                    className="px-1.5 py-0.5 rounded text-xs font-medium"
                    style={{ 
                      backgroundColor: indicatorStats.highestLevel === 'Urgent' ? '#fef2f2' :
                                       indicatorStats.highestLevel === 'High' ? '#fff7ed' :
                                       indicatorStats.highestLevel === 'Medium' ? '#fffbeb' : '#f0fdf4',
                      color: indicatorStats.highestLevel === 'Urgent' ? '#dc2626' :
                             indicatorStats.highestLevel === 'High' ? '#ea580c' :
                             indicatorStats.highestLevel === 'Medium' ? '#854d0e' : '#16a34a'
                    }}
                  >
                    {indicatorStats.highestLevel}
                  </span>
                  {' '}risk indicator{indicatorStats.highestLevelCount !== 1 ? 's' : ''} reported
                </span>
              </li>
            ) : (
              <li className="flex items-center gap-1.5">
                <span className="text-gray-400">•</span>
                <span>No elevated risk indicators reported</span>
              </li>
            )}
            
            {/* Manual review items summary (only show if there are any) */}
            {totalManualReview > 0 && (
              <li className="flex items-center gap-1.5">
                <span className="text-gray-400">•</span>
                <span>
                  <span className="font-semibold">{totalManualReview}</span>
                  {' '}answer{totalManualReview !== 1 ? 's' : ''} needing manual review
                </span>
              </li>
            )}
          </ul>
          
          {/* View Risks CTA */}
          <button
            type="button"
            onClick={() => setIsDialogOpen(true)}
            className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            View Risks
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        </div>
      </OverviewCard>
      
      {/* Risk Stratification Detail Dialog */}
      <RiskStratificationDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        riskData={riskData}
        orgId={orgId}
        patientRecordId={patientRecordId}
        respondentNames={respondentNames}
        canReview={canReview}
        // Note: onRiskDataUpdated is not needed here because the patientRecord
        // is subscribed via Firestore realtime listener, so updates from the
        // Cloud Function will automatically trigger a re-render
      />
    </>
  );
};


/**
 * PeopleInvolvedCard
 * ------------------
 * Wraps the FormProgressSummaryPanel in an OverviewCard to display
 * the people involved in this referral (Parent/Guardian and School).
 * This replaces the "Data Source" card from the design mockup.
 */
const PeopleInvolvedCard = ({
  formInstances,
  triageAssigned,
  respondentNames,
  onOpenDialog,
  schoolPhase,
  intakeConcern,
  patientRecord = null
}) => {
  return (
    <OverviewCard title="People Involved in Referral">
      <FormProgressSummaryPanel
        formInstances={formInstances}
        triageAssigned={triageAssigned}
        respondentNames={respondentNames}
        onOpenDialog={onOpenDialog}
        schoolPhase={schoolPhase}
        intakeConcern={intakeConcern}
        patientRecord={patientRecord}
      />
    </OverviewCard>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Form Progress Summary Panel & Dialog
// Styled to match PeoplePanel / PeopleDialog theming
// ─────────────────────────────────────────────────────────────────────────────

// Status constants are centralised in `src/rms/utils/referralStatus.js`.
// We alias here to minimise churn in the surrounding UI code.
const RESPONDENT_STATUS = STATUS;

/**
 * RespondentStatusBadge - Status badge matching PeoplePanel StatusBadge styling exactly
 */
const RespondentStatusBadge = ({ status }) => {
  const { label, className } = getStatusBadgeConfig(status);
  
  return (
    <span className={`inline-flex min-w-28 items-center justify-center px-2 py-0.5 rounded-full border text-xs font-medium ${className}`}>
      {label}
    </span>
  );
};

/**
 * RespondentCard - Card matching PeoplePanel PersonCard styling
 * Clickable to open the detailed form progress dialog
 * 
 * Props:
 *   label: Display name or fallback (e.g., "Jane Smith" or "Parent / Guardian")
 *   roleLabel: Role subtitle (e.g., "Parent" or "Education Setting")
 *   status: One of RESPONDENT_STATUS values
 *   completedCount: Number of completed forms
 *   totalCount: Total number of forms
 *   placeholder: Message to show when status is NOT_ASSIGNED
 *   onClick: Click handler
 */
const RespondentCard = ({ 
  label, 
  roleLabel,
  status,
  completedCount,
  totalCount,
  placeholder,
  onClick
}) => {
  const isNotAssigned = status === RESPONDENT_STATUS.NOT_ASSIGNED;
  const isInviteSent = status === RESPONDENT_STATUS.INVITE_SENT;
  
  return (
    <button
      type="button"
      onClick={isNotAssigned ? undefined : onClick}
      disabled={isNotAssigned}
      className={`w-full p-4 rounded-xl border border-gray-200 bg-white text-left ${
        isNotAssigned ? 'opacity-75 cursor-default' : 'hover:bg-gray-50 transition-colors'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {/* Avatar - matching PersonCard styling */}
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
            isNotAssigned ? 'bg-gray-100' : 'bg-primary/10'
          }`}>
            <svg className={`w-5 h-5 ${isNotAssigned ? 'text-gray-400' : 'text-primary'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          
          <div className="min-w-0">
            {/* Primary heading */}
            <span className={`text-sm font-semibold ${isNotAssigned ? 'text-gray-500' : 'text-gray-900'}`}>
              {label}
            </span>
            {/* Role subtitle or placeholder/status message */}
            {isNotAssigned ? (
              <p className="text-xs text-gray-400 italic">{placeholder || 'Not yet assigned'}</p>
            ) : isInviteSent ? (
              <>
                {roleLabel && (
                  <p className="text-xs text-gray-500">{roleLabel}</p>
                )}
                <p className="text-xs text-purple-600 mt-1">Awaiting invite acceptance</p>
              </>
            ) : (
              <>
                {roleLabel && (
                  <p className="text-xs text-gray-500">{roleLabel}</p>
                )}
                {/* Form progress info (concise; status shown by the badge) */}
                <p className="text-xs text-gray-500 mt-1">
                  {completedCount}/{totalCount} forms
                </p>
              </>
            )}
          </div>
        </div>
        
        {/* Status badge */}
        <div className="flex flex-col items-end">
          <RespondentStatusBadge status={status} />
        </div>
      </div>
    </button>
  );
};

/**
 * FormProgressSummaryPanel
 * ------------------------
 * Panel showing respondents and their form completion status.
 * Styled to match PeoplePanel theming (space-y-3, rounded-xl cards).
 * 
 * Props:
 *   respondentNames: { guardian: { firstName, lastName }, school_user: { firstName, lastName } }
 */
const FormProgressSummaryPanel = ({
  formInstances,
  triageAssigned,
  respondentNames,
  onOpenDialog,
  schoolPhase,
  intakeConcern,
  // The record picks the HFP set (hfpFormKeysForRecord): Harbour pupils are
  // counted on v3; legacy records keep the instance-based detection.
  patientRecord = null
}) => {
  // Secondary-school referrals skip the HFP entirely (TD-129/TD-137), so HFP
  // forms must not count toward the completion total or status.
  const isSecondary = schoolPhase === 'secondary';
  /**
   * Provider pages store full formInstances (objects), whereas service-user surfaces
   * track a simpler map of progress percentages.
   *
   * To keep *status parity* across the app, we normalise instances into a % map
   * and reuse the shared `calculateRoleStatus` business logic.
   */
  const progressByKey = useMemo(() => {
    const out = {};
    const entries = Object.entries(formInstances || {});
    for (const [key, instance] of entries) {
      // INVARIANT: 100 means submitted; draft progress is capped at 99 so a
      // fully-drafted-but-unsubmitted form never counts as complete (see
      // completionPercent in referralStatus).
      out[key] = completionPercent(
        instance?.status,
        instance?.progress?.percent ?? instance?.progress?.progressPercentage
      );
    }
    return out;
  }, [formInstances]);

  const getLogicalPercent = useCallback((baseKey, role) => {
    // Base referral forms can be stored as either:
    // - role-suffixed (`requestForReferral_school_user`)
    // - or unsuffixed base (`requestForReferral`)
    if (BASE_REFERRAL_FORMS.includes(baseKey)) {
      const suff = `${baseKey}_${role}`;
      if (Object.prototype.hasOwnProperty.call(progressByKey, suff)) return progressByKey[suff];
      if (Object.prototype.hasOwnProperty.call(progressByKey, baseKey)) return progressByKey[baseKey];
      return 0;
    }

    // HFP forms are always role-suffixed. Accept all three instrument
    // generations (v3, v2, legacy v1), so a role whose HFP is stored under any
    // set still reads correctly (matches the dashboard's completion logic).
    if (
      HFP_FORM_KEYS.includes(baseKey) ||
      HFP_V1_FORM_KEYS.includes(baseKey) ||
      HFP_V3_FORM_KEYS.includes(baseKey)
    ) {
      return progressByKey[`${baseKey}_${role}`] ?? 0;
    }

    // Triage forms: canonical storage is role-suffixed (e.g. `theirEarlyLife_guardian`).
    // We still fall back to unsuffixed for legacy/test data.
    if (TRIAGE_FORM_KEYS.includes(baseKey)) {
      const suff = `${baseKey}_${role}`;
      if (Object.prototype.hasOwnProperty.call(progressByKey, suff)) return progressByKey[suff];
      if (Object.prototype.hasOwnProperty.call(progressByKey, baseKey)) return progressByKey[baseKey];
      return 0;
    }

    // Fallback for unexpected keys.
    return progressByKey[`${baseKey}_${role}`] ?? progressByKey[baseKey] ?? 0;
  }, [progressByKey]);

  const calcCounts = useCallback((role) => {
    const logicalKeys = [
      // Autism-only referrals drop the ADHD SNAP-IV scale (TD-139).
      ...effectiveBaseReferralForms(intakeConcern),
      // The strengths and needs forms are the SEND pathway's, never the
      // referral's (TDI-511): they count only while the record's SEND pathway
      // is active, or when this role already holds answers on them (legacy
      // records that collected them before the pathways were separated), on
      // the set this role's data actually uses.
      ...hfpKeysCountedForRole(role, progressByKey, patientRecord),
      ...(role === 'guardian' && triageAssigned ? TRIAGE_FORM_KEYS : []),
    ];

    let completed = 0;
    let started = 0;
    for (const k of logicalKeys) {
      const percent = getLogicalPercent(k, role);
      if (percent === 100) completed += 1;
      else if (percent > 0) started += 1;
    }

    return { completed, total: logicalKeys.length, started };
  }, [getLogicalPercent, triageAssigned, isSecondary, intakeConcern, progressByKey, patientRecord]);

  const guardianInfo = respondentNames?.guardian;
  const guardianCounts = calcCounts('guardian');
  const schoolCounts = calcCounts('school_user');

  const guardianStatus = calculateRoleStatus({
    role: 'guardian',
    formProgress: progressByKey,
    triageAssigned,
    hasGuardian: !!guardianInfo,
    guardianInviteStatus: guardianInfo?.participantStatus || null,
    schoolPhase,
    intakeConcern,
    patient: patientRecord,
  });

  const schoolProgress = {
    ...schoolCounts,
    status: calculateRoleStatus({
      role: 'school_user',
      formProgress: progressByKey,
      triageAssigned: false,
      schoolPhase,
      intakeConcern,
      patient: patientRecord,
    }),
  };

  const guardianProgress = {
    ...guardianCounts,
    status: guardianStatus,
  };

  // Build display names and roles from respondentNames prop
  const guardianName = guardianInfo
    ? `${guardianInfo.firstName || ''} ${guardianInfo.lastName || ''}`.trim()
    : null;
  const guardianRole = guardianInfo?.relationshipLabel || 'Parent / Carer';
  
  const schoolUserInfo = respondentNames?.school_user;
  const schoolUserName = schoolUserInfo
    ? `${schoolUserInfo.firstName || ''} ${schoolUserInfo.lastName || ''}`.trim()
    : null;
  const schoolUserRole = schoolUserInfo?.role || 'Education Setting';

  return (
    <div className="space-y-3">
      {/* School card - shown first as they initiate the referral */}
      <RespondentCard
        label={schoolUserName || 'School Representative'}
        roleLabel={schoolUserRole}
        status={schoolProgress.status}
        completedCount={schoolProgress.completed}
        totalCount={schoolProgress.total}
        onClick={() => onOpenDialog('school_user')}
      />

      {/* Parent/Guardian card */}
      <RespondentCard
        label={guardianName || 'Parent/Carer'}
        roleLabel={guardianRole}
        status={guardianProgress.status}
        completedCount={guardianProgress.completed}
        totalCount={guardianProgress.total}
        placeholder="No parent/carer has been nominated yet"
        onClick={() => onOpenDialog('guardian')}
      />
    </div>
  );
};

/**
 * FormProgressDialog
 * ------------------
 * Dialog showing detailed form progress for a specific respondent.
 * Styled to match PeopleDialog theming.
 * 
 * Props:
 *   respondentNames: { guardian: { firstName, lastName }, school_user: { firstName, lastName } }
 */
const FormProgressDialog = ({
  isOpen,
  onClose,
  role,
  formInstances,
  triageAssigned,
  orgId,
  patientRecordId,
  respondentNames,
  schoolPhase,
  intakeConcern,
  onAutoComplete,
  onViewDetails,
  // Per-form PDF download for EMIS entry (referral forms and triage
  // questionnaires only — never the HFP). downloadPendingKey names the
  // document being prepared ('form:<instanceId>'), if any.
  onDownload,
  downloadPendingKey = null,
  // The record picks the HFP set (hfpFormKeysForRecord): Harbour pupils list
  // the v3 forms; legacy records keep the instance-based detection.
  patientRecord = null
}) => {
  if (!isOpen) return null;

  const isGuardian = role === 'guardian';
  // Secondary-school referrals skip the HFP, so its forms are not shown (TD-137).
  const isSecondary = schoolPhase === 'secondary';

  // Build the display name from respondentNames prop
  const respondent = isGuardian ? respondentNames?.guardian : respondentNames?.school_user;
  const respondentName = respondent
    ? `${respondent.firstName || ''} ${respondent.lastName || ''}`.trim()
    : null;
  const roleLabel = respondentName || (isGuardian ? 'Parent/Carer' : 'School Representative');

  // Referral forms for this concern: autism-only referrals drop the ADHD SNAP-IV
  // scale (TD-139), so filter the static referral list to the effective set.
  const applicableBase = effectiveBaseReferralForms(intakeConcern);
  const referralForms = FORM_GROUPS.referral.forms.filter((f) => applicableBase.includes(f.key));

  // HFP rows for this role, derived from whichever instrument generation
  // (v3/v2/v1) the record's instances actually hold — the same detection the
  // respondent-card counters use. A static v2 list here rendered every HFP row
  // as empty/0% (view disabled) on v3-only records.
  const hfpProgressByKey = {};
  for (const [key, instance] of Object.entries(formInstances || {})) {
    hfpProgressByKey[key] = completionPercent(
      instance?.status,
      instance?.progress?.percent ?? instance?.progress?.progressPercentage
    );
  }
  // The strengths and needs rows belong to the SEND pathway (TDI-511): shown
  // while that pathway is active, or when this role already holds answers on
  // them, never as part of a referral that did not ask for them.
  const hfpForms = hfpKeysCountedForRole(role, hfpProgressByKey, patientRecord).length
    ? hfpFormsForRoleData(role, hfpProgressByKey, patientRecord)
    : [];
  const forms = isGuardian
    ? [
        ...referralForms,
        ...hfpForms,
        ...(triageAssigned ? FORM_GROUPS.triage.forms : [])
      ]
    : [
        ...referralForms,
        ...hfpForms
      ];

  // Group forms by category
  const hfpGroup = hfpForms.length
    ? [{ title: 'Strengths and needs profile (SEND pathway)', forms: hfpForms }]
    : [];
  const formGroups = isGuardian
    ? [
        { title: 'Referral Forms', forms: referralForms },
        ...hfpGroup,
        ...(triageAssigned ? [{ title: 'Triage Forms', forms: FORM_GROUPS.triage.forms }] : [])
      ]
    : [
        { title: 'Referral Forms', forms: referralForms },
        ...hfpGroup
      ];

  // Resolve each logical form's backing doc via the shared role-suffixed-first
  // resolver — the header count and the per-form rows below MUST use the same
  // resolution or they disagree (a hand-rolled version here looked up triage
  // forms unsuffixed and reported 4/11 while every row showed complete).
  const getInstanceForLogicalForm = (baseKey) => resolveRoleFormInstance(formInstances, role, baseKey);

  // Calculate overall progress
  const completed = forms.filter(f => getInstanceForLogicalForm(f.key)?.status === 'submitted').length;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
        onClick={onClose}
      />
      
      {/* Dialog content */}
      <div className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-[480px] max-h-[85vh] bg-white rounded-xl shadow-2xl p-4 md:p-6 overflow-y-auto text-left">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="text-left">
            <h2 className="text-lg font-bold text-gray-900 text-left">
              {roleLabel} Forms
            </h2>
            <p className="text-sm text-gray-500 text-left">
              {completed} of {forms.length} complete
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Form list - grouped */}
        <div className="space-y-4 text-left">
          {formGroups.map((group, groupIdx) => (
            <div key={groupIdx}>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 text-left">
                {group.title}
              </h3>
              <div className="rounded-lg overflow-hidden border border-gray-100">
                {group.forms.map((form, formIdx) => {
                  const instance = getInstanceForLogicalForm(form.key) || {};
                  // The doc id backing the resolved instance: suffixed when that
                  // doc exists (current flows), bare for legacy single docs.
                  const instanceId = formInstances?.[`${form.key}_${role}`]
                    ? `${form.key}_${role}`
                    : form.key;
                  const status = instance.status || 'not_started';
                  const percent = instance.progress?.percent || 0;
                  const isComplete = status === 'submitted';
                  const hasAnswers = Object.keys(instance.answers || {}).length > 0;
                  const hasData = hasAnswers || percent > 0;
                  const canDownload = Boolean(onDownload) && isExportableFormKey(form.key);
                  const downloading = downloadPendingKey === `form:${instanceId}`;

                  return (
                    <div
                      key={form.key}
                      className={`flex items-center gap-3 py-2 px-3 transition-colors ${
                        formIdx % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                      }`}
                    >
                      {/* Form name */}
                      <span className="text-xs font-medium text-gray-800 flex-1 min-w-0 truncate text-left">
                        {form.title}
                      </span>
                      
                      {/* Progress bar */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-300 ${
                              isComplete ? 'bg-green-500' : percent > 0 ? 'bg-amber-500' : 'bg-gray-300'
                            }`}
                            style={{ width: `${isComplete ? 100 : percent}%` }}
                          />
                        </div>
                        {isComplete ? (
                          <CheckCircleIcon className="ml-2 w-4 h-4 text-green-600 flex-shrink-0" />
                        ) : (
                          <span className="text-xs text-gray-400 w-6 text-right">{percent}%</span>
                        )}
                      </div>
                      
                      {/* Action buttons */}
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        {/* View details button */}
                        <button
                          type="button"
                          onClick={() => onViewDetails(form.key, form.title, instanceId, instance, role)}
                          title={hasData ? 'View form details' : 'No data to view'}
                          className={`p-1.5 rounded transition-colors ${
                            hasData 
                              ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100' 
                              : 'text-gray-200 cursor-not-allowed'
                          }`}
                          disabled={!hasData}
                        >
                          <SearchIcon className="w-4 h-4" />
                        </button>

                        {canDownload && (
                          <button
                            type="button"
                            onClick={() => onDownload(instanceId)}
                            aria-label={downloading ? `Preparing ${form.title} PDF` : `Download ${form.title} as PDF`}
                            title={hasAnswers ? 'Download as PDF' : 'No answers to download yet'}
                            className={`p-1.5 rounded transition-colors ${
                              hasAnswers && !downloadPendingKey
                                ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                : 'text-gray-200 cursor-not-allowed'
                            }`}
                            disabled={!hasAnswers || Boolean(downloadPendingKey)}
                          >
                            {downloading ? (
                              <RefreshIcon className="w-4 h-4 animate-spin text-gray-400" />
                            ) : (
                              <DownloadIcon className="w-4 h-4" />
                            )}
                          </button>
                        )}

                        {/* Auto-complete button (dev only) */}
                        {isDev && (
                          <AutoCompleteButton
                            formKey={form.key}
                            role={role}
                            orgId={orgId}
                            patientRecordId={patientRecordId}
                            onComplete={onAutoComplete}
                            isComplete={isComplete}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default function RmsPatientOverview() {
  const navigate = useNavigate();
  const { patientRecordId } = useParams();
  const [searchParams] = useSearchParams();
  const { claims, user } = useAuth();
  const orgId = claims?.rmsOrgId;

  const [patientRecord, setPatientRecord] = useState(null);
  const [formInstances, setFormInstances] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Consent withdrawn (TDI-475 / 476 / 481): the listener's error (the rules
  // shut it on withdrawal), whether the non-clinical side chose to read the
  // record anyway, and the record-a-withdrawal form.
  const [listenerError, setListenerError] = useState(null);
  // R-038 (TDI-758): the server's refusal when this referral belongs to the
  // other service. The overview shows it with "Go to dashboard"; no data is
  // sent.
  const [specialismRefusal, setSpecialismRefusal] = useState(null);
  const [readOnlyWithdrawn, setReadOnlyWithdrawn] = useState(false);
  const [showRecordWithdrawal, setShowRecordWithdrawal] = useState(false);
  const [startingTriage, setStartingTriage] = useState(false);
  // Confirm dialog shown before a clinician starts triage, making the
  // assignment + lock explicit.
  const [showStartConfirm, setShowStartConfirm] = useState(false);
  const [unassigningTriage, setUnassigningTriage] = useState(false);
  // Required when releasing another clinician's triage — the server emails it
  // to the affected clinician (LSC-033: a force-release must never be silent).
  const [releaseReason, setReleaseReason] = useState('');
  // TD-244: releasing a triage back to the pool discards any unsubmitted
  // decision progress (the wizard holds its state in memory until submit, and
  // the next reviewer starts from a blank slate per the second-review
  // independence model). Confirm before releasing so that loss is deliberate.
  const [showReleaseConfirm, setShowReleaseConfirm] = useState(false);
  // Ready-for-triage decision dialog (LSC-038/LSC-044). Marking a referral
  // ready is the active, audited decision that moves it into the triage queue;
  // when forms are incomplete the dialog requires a reason.
  const [showMarkReadyConfirm, setShowMarkReadyConfirm] = useState(false);
  const [markReadyReason, setMarkReadyReason] = useState('');
  const [markingReady, setMarkingReady] = useState(false);

  // State for form details dialog
  const [detailsDialog, setDetailsDialog] = useState({
    isOpen: false,
    formKey: null,
    formTitle: null,
    instanceId: null,
    formInstance: null,
    role: null,
  });

  // State for form progress dialog (shows detailed form list for a respondent)
  const [formProgressDialog, setFormProgressDialog] = useState({
    isOpen: false,
    role: null, // 'guardian' or 'school_user'
  });

  // State for the assign triage forms dialog (for testing purposes)
  const [showAssignTriageDialog, setShowAssignTriageDialog] = useState(false);


  // State for respondent info (names and relationship, fetched from participants API and school users collection)
  // Used for displaying respondent details in risk stratification and form progress panels
  const [respondentNames, setRespondentNames] = useState({
    guardian: null,      // { firstName, lastName, relationship, relationshipLabel } or null
    school_user: null,   // { firstName, lastName, role } or null
  });
  // B13 / RMS-59: staff-side editing of the nominated parent/carer's details.
  // Bumping the refresh counter refetches respondentNames after a save (the
  // participant edit does not touch the patientRecord doc the effect watches).
  const [showEditGuardian, setShowEditGuardian] = useState(false);
  const withdrawnStop = useConsentWithdrawnStop({ patientRecord, listenerError });
  const [participantsRefresh, setParticipantsRefresh] = useState(0);

  // Callback for when a form is auto-completed (optional toast/notification)
  const handleAutoComplete = useCallback((formKey, role, resultData) => {
    console.log(`[RmsPatientOverview] Form auto-completed: ${formKey} for ${role}`);
    // Form instances will auto-refresh via the Firestore snapshot listener
    
    // Log risk calculation result if present
    if (resultData?.riskCalculated) {
      console.log(`[RmsPatientOverview] 🎯 Risk stratification calculated! Tier: ${resultData.riskTier}`);
    } else {
      console.log(`[RmsPatientOverview] Risk not calculated yet (waiting for all 7 triage forms)`);
    }
  }, []);

  // Callback to open the form details dialog. `role` is the respondent whose
  // submission is shown ('school_user' | 'guardian') so the dialog can hide
  // sections the other respondent answers.
  const handleViewDetails = useCallback((formKey, formTitle, instanceId, formInstance, role) => {
    setDetailsDialog({
      isOpen: true,
      formKey,
      formTitle,
      instanceId,
      formInstance,
      role: role || null,
    });
  }, []);

  // Separate PDFs for EMIS entry: one per form instance (never the HFP) and
  // one per pathway's completed triage decision.
  const {
    pendingKey: documentDownloadPendingKey,
    downloadForm,
    downloadDecision,
    confirmDialog: downloadConfirmDialog,
  } = useReferralDocumentDownload({ orgId, patientRecordId, patientRecord });
  const triageDecisionDownloads = useMemo(
    () => downloadableTriageDecisions(patientRecord),
    [patientRecord],
  );
  // The menu closes on select, so say the download is on its way.
  const handleDownloadDecision = useCallback((pathway) => {
    toast.info('Preparing the PDF. It will download in a few seconds.');
    downloadDecision(pathway);
  }, [downloadDecision]);

  // Callback to close the form details dialog
  const handleCloseDetails = useCallback(() => {
    setDetailsDialog((prev) => ({ ...prev, isOpen: false }));
  }, []);

  // Callback to open the form progress dialog for a specific role
  const handleOpenFormProgressDialog = useCallback((role) => {
    setFormProgressDialog({ isOpen: true, role });
  }, []);

  // Callback to close the form progress dialog
  const handleCloseFormProgressDialog = useCallback(() => {
    setFormProgressDialog({ isOpen: false, role: null });
  }, []);

  // Callback when triage forms are successfully assigned
  const handleTriageAssigned = useCallback(() => {
    console.log('[RmsPatientOverview] Triage forms assigned successfully');
    // Patient record updates via realtime listener, no manual refresh needed
  }, []);

  // Start triage: assign the calling clinician + lock the patient (TD-28/29),
  // then open the proforma. The server rejects if it is already in triage by
  // someone else; surface that and stay put.
  const handleStartTriage = useCallback((explicitPathway) => {
    if (!orgId || !patientRecordId) return;
    // Combined (ADHD + autism) referrals are triaged per pathway, so the server
    // requires a pathway. Resolve it exactly as the proforma does — the active
    // tab carried in via ?pathway=, else the clinician's own specialism — so a
    // single-specialism clinician starting triage on a combined referral acts
    // on their pathway instead of hitting "pathway is required". A
    // both-specialism clinician with no tab context resolves to null, so the
    // confirm dialog asks which pathway and passes it here explicitly (TD-173).
    // For a single-concern referral this resolves to null and the server
    // ignores it.
    const actingPathway =
      explicitPathway === 'adhd' || explicitPathway === 'asd'
        ? explicitPathway
        : resolveActingPathway(
            patientRecord,
            searchParams.get('pathway'),
            claims?.specialism,
          );
    // Navigate into the proforma immediately so the clinician isn't left waiting
    // on the callable (which can cold-start); acquire the lock in the background.
    // Editability isn't gated on the lock and the decision callable still
    // enforces the assignee server-side. On failure, return to the overview.
    setShowStartConfirm(false);
    const pathwayQs = actingPathway ? `?pathway=${actingPathway}` : '';
    navigate(`/rms/provider/patient/${patientRecordId}/triage${pathwayQs}`);
    apiStartTriage({ orgId, patientRecordId, pathway: actingPathway }).catch((err) => {
      toast.error(err?.message || 'Could not start triage');
      navigate(`/rms/provider/patient/${patientRecordId}${pathwayQs}`);
    });
  }, [orgId, patientRecordId, navigate, patientRecord, searchParams, claims]);

  // Release a patient from triage (TD-28). Allowed for the assignee (self) or an
  // Org. Admin / Support; enforced server-side. The realtime listener updates UI.
  const handleUnassignTriage = useCallback(async () => {
    if (!orgId || !patientRecordId) return;
    setUnassigningTriage(true);
    try {
      // Combined referrals hold a per-pathway lock, and the server requires the
      // pathway to release it (ignored for single-concern referrals). Resolve
      // it the same way handleStartTriage does.
      const pathway = resolveActingPathway(
        patientRecord,
        searchParams.get('pathway'),
        claims?.specialism,
      );
      await apiUnassignTriage({ orgId, patientRecordId, pathway, reason: releaseReason });
      toast.success('Triage released');
      setShowReleaseConfirm(false);
      setReleaseReason('');
    } catch (err) {
      toast.error(err?.message || 'Could not release triage');
    } finally {
      setUnassigningTriage(false);
    }
  }, [orgId, patientRecordId, patientRecord, searchParams, claims, releaseReason]);

  // Subscribe to patient record (realtime updates for triageFormsAssigned, riskStratification, etc.)
  useEffect(() => {
    // orgId is derived from auth claims which load asynchronously;
    // wait for it rather than showing an error during the loading window
    if (!orgId || !patientRecordId) return;

    const patientRef = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);
    
    const unsubscribe = onDocSnapshot(
      patientRef,
      (snap) => {
        if (!snap.exists()) {
          setError('Patient record not found');
          setLoading(false);
          return;
        }
        setPatientRecord({ id: snap.id, ...snap.data() });
        setLoading(false);
      },
      (err) => {
        console.error('[RmsPatientOverview] Error loading patient:', err);
        // The rules shut this listener before the record can say why, and a
        // denial now has two meanings: a withdrawal cleared the clinical
        // access grant (TDI-475), or the referral belongs to the other
        // service (R-038, TDI-758). One gated callable tells them apart: it
        // runs the same server rule and names the service in its refusal.
        if (String(err?.code || '').includes('permission-denied')) {
          apiGetReferralParticipants({ orgId, patientRecordId }).catch((probe) => {
            if (probe?.details?.reason === 'outside_specialism') {
              setSpecialismRefusal(probe?.message
                || 'This referral is for the other service. Only that team and the org admins can open it.');
            } else {
              setListenerError(err);
            }
          });
        } else {
          setListenerError(err);
          setError('Failed to load patient record');
        }
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [orgId, patientRecordId]);

  // Subscribe to the form instances a provider may read (TDI-574): the whole
  // subcollection once the family has shared the profile, otherwise the
  // non-HFP instances one by one, since the rule withholds the strengths and
  // needs answers and a collection read would be refused outright.
  const profileSharedFlag = profileSharedForRules(patientRecord);
  const nonHfpIdsKey = patientRecord ? nonHfpInstanceIds(patientRecord).join(',') : '';
  useEffect(() => {
    if (!orgId || !patientRecordId || !patientRecord) return undefined;

    const unsubscribe = subscribeProviderFormInstances(
      { orgId, patientRecordId, record: patientRecord },
      (snapshot) => setFormInstances(instancesById(snapshot)),
      (err) => {
        console.error('[RmsPatientOverview] Error loading form instances:', err);
      }
    );

    return () => unsubscribe();
    // The listener set depends only on the flag and the ids, never on every
    // record change: the record streams while the clinician works.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, patientRecordId, profileSharedFlag, nonHfpIdsKey]);

  // Fetch respondent names and relationship info (guardian from participants API, school user from schoolUsers collection)
  useEffect(() => {
    let cancelled = false;

    // Map relationship IDs to human-readable labels (matches backend mapping)
    const relationshipLabels = {
      'parent': 'Parent',
      'step_parent': 'Step-parent',
      'foster_carer': 'Foster carer',
      'kinship_carer': 'Kinship carer',
      'legal_guardian': 'Legal guardian',
      'informal_carer': 'Informal carer',
      'school_rep': 'School Representative',
    };

    const fetchRespondentNames = async () => {
      if (!orgId || !patientRecordId || !patientRecord) return;

      try {
        // Fetch guardian name and relationship from participants API
        const { participants } = await apiGetReferralParticipants({ orgId, patientRecordId });
        const guardians = (participants || []).filter(p => p.role === 'guardian');
        
        if (!cancelled && guardians.length > 0) {
          // Filter out superseded guardians (email was changed before invite consumed)
          const activeGuardians = guardians.filter(g => g.status !== 'superseded');
          
          if (activeGuardians.length > 0) {
            const guardian = activeGuardians[0]; // Use first active guardian
            const relationshipId = guardian.relationship || null;
            const relationshipLabel = relationshipId ? (relationshipLabels[relationshipId] || relationshipId) : null;
            
            // Determine if account is linked (invite has been consumed)
            // Status values: nominated, invite_sent, account_linked
            const isAccountLinked = guardian.status === PARTICIPANT_STATUS.ACCOUNT_LINKED;
            
            setRespondentNames(prev => ({
              ...prev,
              guardian: {
                firstName: guardian.firstName || '',
                lastName: guardian.lastName || '',
                relationship: relationshipId,
                relationshipLabel: relationshipLabel,
                participantStatus: guardian.status || null, // Store raw participant status
                isAccountLinked: isAccountLinked,
              }
            }));
          }
        }

        // Fetch school user name from schoolUsers collection
        const createdByUserId = patientRecord?.createdByUserId;
        if (createdByUserId) {
          const schoolUsersQuery = query(
            collection(db, 'rms_organisations', orgId, 'schoolUsers'),
            where('accountId', '==', createdByUserId)
          );
          const schoolUserSnap = await getDocs(schoolUsersQuery);
          
          if (!cancelled && !schoolUserSnap.empty) {
            const schoolUserData = schoolUserSnap.docs[0].data() || {};
            setRespondentNames(prev => ({
              ...prev,
              school_user: {
                firstName: schoolUserData.firstName || '',
                lastName: schoolUserData.lastName || '',
                role: 'School Representative', // Default role label for school users
              }
            }));
          }
        }
      } catch (err) {
        console.error('[RmsPatientOverview] Error fetching respondent names:', err);
      }
    };

    fetchRespondentNames();

    return () => { cancelled = true; };
  }, [orgId, patientRecordId, patientRecord, participantsRefresh]);

  // Derived data
  const patientName = getPatientName(patientRecord);
  const referralDate = formatDateShort(patientRecord?.createdAt, '-');
  // "Received" is when the referral entered the triage queue: triageReadiness
  // is written by the backend when the last required form is submitted (auto)
  // or a clinician marks it ready (manual). createdAt is merely "started" —
  // surfacing it as the received date skewed customers' KPIs.
  const referralReceivedDate = patientRecord?.triageReadiness?.markedAt
    ? formatDateShort(patientRecord.triageReadiness.markedAt, null)
    : null;
  // const referrerType = patientRecord?.referrerType || 'unknown';
  // Keep parity with service-user surfaces:
  // - If the patientRecord flag is set, triage is assigned.
  // - If the flag is missing/stale but triage formInstances exist, we still treat triage as assigned.
  const triageAssigned =
    patientRecord?.triageFormsAssigned === true ||
    Object.keys(formInstances || {}).some((k) => isTriageFormInstanceKey(k));
  const riskStratification = patientRecord?.riskStratification || null;
  // Only the record's own instrument's scoring is shown (hfpInstrumentForRecord):
  // Harbour pupils answer v3 and v4 records have their own card, so a v2 scoring
  // on those records is never shown — until their own scoring lands the card
  // reads as pending, exactly like a legacy record with no scoring yet.
  const hfpInstrument = hfpInstrumentForRecord(patientRecord);
  const hfpV2 = hfpInstrument === 'v2' ? (patientRecord?.domains?.hfpV2 || null) : null;
  // HFP v3 (the 157-item instrument, TD-200): records scored under v3 render
  // the home-vs-school matrix; older records keep the v2 summary unchanged. A
  // record with a stored instrument (TDI-414) only shows v3 if it is on v3.
  const hfpV3 = hasStoredHfpInstrument(patientRecord) && hfpInstrument !== 'v3'
    ? null
    : (patientRecord?.domains?.hfpV3 || null);
  // TDI-483: whether the family has shared the profile with the clinical
  // team. Read live from the record, so a change takes effect at once.
  const profileShared = profileSharedWithClinicians(patientRecord);
  // Pain safety override (instruction section 8 [build]): surfaces in the
  // Reported Risk Indicators card whenever either context's Interoception
  // record fired the override. Null for v2 records — the card is unchanged.
  // TDI-483: this line is profile content ("Reduced pain awareness (HFP sensory
  // profile, home)"), so it follows the family's answer like every other
  // profile surface. Without the gate it was the one piece of the profile a
  // clinician still saw on a record the family had said no to.
  const hfpV3PainOverride = useMemo(() => {
    if (!profileShared) return null;
    const senses = (ctx) => hfpV3?.scoring?.contexts?.[ctx]?.senses?.Interoception;
    const home = !!senses('home')?.painOverride;
    const school = !!senses('school')?.painOverride;
    if (!home && !school) return null;
    return {
      contexts: home && school ? '' : home ? ', home' : ', school',
    };
  }, [hfpV3, profileShared]);
  // Secondary-school referrals skip the HFP (TD-129/TD-137); used to hide HFP
  // forms and exclude them from the completion counts/status. The referral doesn't
  // always carry an explicit school phase, so when it's absent fall back to the
  // CYP's age — UK secondary school begins at age 11 (Year 7) — so secondary-age
  // CYPs still correctly skip the HFP.
  const explicitSchoolPhase = patientRecord?.schoolPhase || patientRecord?.school?.schoolType || null;
  const ageForPhase = Number(patientRecord?.subject?.age);
  const schoolPhase =
    explicitSchoolPhase ||
    (Number.isFinite(ageForPhase) && ageForPhase >= 11 ? 'secondary' : null);
  // Autism-only referrals drop the ADHD SNAP-IV scale (TD-139).
  const intakeConcern = patientRecord?.intakeConcern || null;
  
  // Referrer details for display in header
  const referrerName = patientRecord?.referrer?.fullName || null;
  // const referrerRelationship = patientRecord?.referrer?.relationshipLabel || null;
  
  // Additional patient details from subject. Sex/gender are promoted onto the
  // subject from the requestForReferral forms on submit (see rms_forms
  // _promote_subject_demographics); DOB comes from the subject.
  const patientSex = patientRecord?.subject?.sex || null;
  const patientGender = patientRecord?.subject?.gender || null;
  const patientDob = patientRecord?.subject?.dob || null;
  // The approved pilot GP practice the school chose at referral start
  // (TDI-488) — ADHD and combined referrals only; absent on older records.
  const patientGpPractice = patientRecord?.referralPathway?.gpPractice?.name || null;
  // Age isn't stored (it changes over time) — derive it from DOB when the record
  // doesn't carry an explicit age.
  const calcAge = (dobStr) => {
    if (!dobStr) return null;
    const d = new Date(dobStr);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    let a = now.getFullYear() - d.getFullYear();
    const md = now.getMonth() - d.getMonth();
    if (md < 0 || (md === 0 && now.getDate() < d.getDate())) a -= 1;
    return a >= 0 && a < 150 ? a : null;
  };
  const patientAge = patientRecord?.subject?.age ?? calcAge(patientDob);
  // Education setting for the header (e.g. "Mainstream Primary School"). Prefer a
  // named school; fall back to the school named in the referral form, then to a
  // phase label derived from schoolPhase.
  const educationSetting =
    patientRecord?.school?.schoolName ||
    patientRecord?.school?.name ||
    patientRecord?.educationSetting ||
    formInstances?.requestForReferral_school_user?.answers?.school_name ||
    formInstances?.requestForReferral_guardian?.answers?.school_name ||
    (schoolPhase ? `${schoolPhase.charAt(0).toUpperCase()}${schoolPhase.slice(1)} School` : null);

  // Determine if ready for review (triage forms assigned and some data present)
  const isReadyForReview = triageAssigned;

  // Triage assignment / lock state (TD-28/29/40).
  // Combined (ADHD + autism) referrals hold their lock/status and reviews per
  // pathway (triagePathways.{pathway}), so read the state for the pathway this
  // clinician is acting on — otherwise the record-level `triage` reads as "not
  // in triage" mid-triage and the Resume/Release actions disappear. For
  // single-concern referrals these helpers resolve to the top-level fields.
  const actingTriagePathway = resolveActingPathway(
    patientRecord,
    searchParams.get('pathway'),
    claims?.specialism,
  );
  const triage = pathwayTriage(patientRecord, actingTriagePathway) || {};

  // Admin Handover (TDI-386): while any pathway waits in Admin Handover, the
  // overview offers the way back to the guided handover screen. Post-triage
  // stages classify without completion data, so null is safe here; the first
  // handover pathway names the one the button opens on.
  const handoverPathway = useMemo(() => {
    if (!patientRecord) return null;
    return pathwaysForConcern(patientRecord.intakeConcern)
      .find((p) => classifyReferralStage(patientRecord, null, p) === 'ready-for-handover') || null;
  }, [patientRecord]);
  const inTriage = triage.status === 'in_triage' && Boolean(triage.assignedClinicianId);
  const assignedToMe = inTriage && triage.assignedClinicianId === user?.uid;
  const assignedToOther = inTriage && !assignedToMe;
  // TD-27: gate triage on the clinician's specialism vs the patient's intake
  // concern. The server (rmsStartTriage) enforces this too; here we disable the
  // action and explain why so a clinician who reaches the page by direct link
  // isn't left with a button that errors.
  const concernInSpecialism = canAccessConcern(claims?.specialism, patientRecord?.intakeConcern);
  const canStartTriage = canTriage(claims) && concernInSpecialism;
  const outOfSpecialism = canTriage(claims) && !concernInSpecialism;
  const canForceUnassign = ['RMS Org. Admin', 'RMS Support'].includes(claims?.userType);
  // Once the triage is terminal (complete) or this clinician has already
  // submitted a decision, they can only *view* it — Resume/Release are no longer
  // offered (re-entry is server-blocked in rmsStartTriage). Show "Show triage".
  const triageComplete = triage.status === 'complete';
  const triageReviewsArr = pathwayReviews(patientRecord, actingTriagePathway);
  const hasReviewedByMe = triageReviewsArr.some((r) => (r?.decidedBy?.clinicianId) === user?.uid);
  const triageViewOnly = triageComplete || hasReviewedByMe;
  // TDI-695: the clinician who recorded the decision may change it until the
  // letter is signed off (an autism first rejection: until a second reviewer
  // picks it up). Mirrors the server rule in rms_triage_reopen.py.
  const decisionChange = triageDecisionChange(patientRecord, actingTriagePathway, user?.uid);
  // Ready-for-triage decision state (LSC-038/LSC-044). A referral only enters
  // the triage queue once a provider user actively marks it ready — form
  // completion alone is never enough, and rmsStartTriage enforces the same
  // rule server-side. Records with triage already underway or recorded predate
  // the gate and are left alone.
  const readyForTriage = patientRecord?.triageReadiness?.status === 'ready';
  const anyTriageActivity = inTriage || triageComplete || triageReviewsArr.length > 0;
  const userCanMarkReady = canMarkReadyForTriage(claims);

  // Whether every requested form is complete (referral + HFP forms for both
  // respondents, and all triage forms once assigned) — mirrors the dashboard
  // classifier's completeness rule so the ready dialog and the queue agree.
  // When false, marking ready requires a recorded reason.
  const allRequestedFormsComplete = useMemo(() => {
    const progress = {};
    for (const [key, instance] of Object.entries(formInstances || {})) {
      progress[key] = completionPercent(
        instance?.status,
        instance?.progress?.percent ?? instance?.progress?.progressPercentage,
      );
    }
    // A referral asks for its own forms only (TDI-495).
    return (
      isRoleReferralFormsComplete('school_user', progress, intakeConcern) &&
      isRoleReferralFormsComplete('guardian', progress, intakeConcern) &&
      triageAssigned &&
      areAllTriageFormsComplete(progress)
    );
  }, [formInstances, intakeConcern, triageAssigned]);

  // Hard preconditions for triage, enforced server-side by
  // rmsMarkReadyForTriage and mirrored here so the reason is visible before
  // anyone clicks. Unlike outstanding forms, these two cannot be overridden
  // with a recorded reason: no explanation a person could type would supply
  // the missing consent or the missing pathway.
  //
  // consent is written onto the record from the initiation form's
  // consent_confirmed answer; referrals created before that was captured
  // carry nothing, which reads as "not recorded" rather than "refused".
  // The reason half is read the same way the server reads it: whether a form
  // was submitted, and separately whether a pathway came out of it. Checking
  // intakeConcern alone would disagree with the server on a referral whose
  // form is submitted but empty, and the person would meet an enabled button
  // that then fails.
  const readinessBlockers = useMemo(() => {
    const blockers = [];
    const consent = patientRecord?.consent;
    if (!consent) {
      blockers.push('Parent or carer consent has not been recorded for this referral.');
    } else if (consent.confirmed !== true) {
      blockers.push('The referrer did not confirm that parent or carer consent had been given.');
    }
    const reasonSubmitted = ['_school_user', '_guardian', ''].some(
      (suffix) => formInstances?.[`reasonForReferral${suffix}`]?.status === 'submitted',
    );
    if (!reasonSubmitted) {
      blockers.push('The reason for referral has not been submitted.');
    } else if (!intakeConcern) {
      blockers.push('No pathway could be worked out from the reason for referral.');
    }
    return blockers;
  }, [patientRecord?.consent, intakeConcern, formInstances]);

  // Mark the referral Ready for triage (LSC-038/LSC-044). The realtime patient
  // listener picks up the resulting triageReadiness change, so the queue
  // position and header update without a manual refresh.
  const handleMarkReadyForTriage = useCallback(async () => {
    if (!orgId || !patientRecordId) return;
    const reason = markReadyReason.trim();
    if (!allRequestedFormsComplete && !reason) return;
    setMarkingReady(true);
    try {
      await apiMarkReadyForTriage({
        orgId,
        patientRecordId,
        formsComplete: allRequestedFormsComplete,
        ...(allRequestedFormsComplete ? {} : { incompleteReason: reason }),
      });
      toast.success('Referral marked ready for triage');
      setShowMarkReadyConfirm(false);
      setMarkReadyReason('');
    } catch (err) {
      toast.error(err?.message || 'Could not mark the referral ready for triage');
    } finally {
      setMarkingReady(false);
    }
  }, [orgId, patientRecordId, allRequestedFormsComplete, markReadyReason]);
  // Second-review state: exactly one review so far, it was a rejection, and the
  // triage is NOT yet complete — i.e. the autism dual-review flow awaiting a
  // second opinion. An ADHD rejection completes the triage in one decision
  // (status 'complete'), so it is a discharge, not a pending second review, and
  // must not show the "awaiting second opinion" banner.
  const firstRejection = !triageComplete
    && triageReviewsArr.length === 1 && triageReviewsArr[0]?.accepted === false
    ? triageReviewsArr[0]
    : null;
  const viewerIsFirstReviewer = Boolean(firstRejection && firstRejection.decidedBy?.clinicianId === user?.uid);
  // LSC-039/LSC-040, TDI-403: while a second review is pending, the first
  // reviewer's identity is withheld from whoever is forming the second
  // opinion, so it is judged on its content. The rule itself lives in
  // decisionAttribution so it can be reasoned about on its own.
  const showFirstReviewerName = canSeeFirstReviewerName({
    isFirstReviewer: viewerIsFirstReviewer,
    userType: claims?.userType,
    isAssignedSecondReviewer: Boolean(firstRejection && assignedToMe),
  });
  const firstReviewerName = showFirstReviewerName
    ? (firstRejection?.decidedBy?.clinicianName || WITHHELD_REVIEWER_NAME)
    : WITHHELD_REVIEWER_NAME;

  // Terminal triage outcome (TD-141): once triage is complete, make the recorded
  // decision and decider unmistakable on the overview. Awaiting-a-second-opinion
  // is handled by the firstRejection banner above and is not terminal here.
  const {
    outcome: decisionOutcome,
    deciderName: decisionDeciderName,
    decidedAt: decisionDecidedAt,
  } = getTriageDecisionSummary(triageReviewsArr, { triageComplete });

  // TD-129 AC3: a combined (ADHD + autism) referral is triaged independently per
  // pathway, so show each pathway's status as its own flag (e.g. "ADHD: Accepted
  // · Autism: In triage"). Single-concern referrals keep the single status below.
  const showPathwayStatusFlags = isCombinedConcern(intakeConcern);
  const pathwayStatusFlags = showPathwayStatusFlags
    ? pathwaysForConcern(intakeConcern).map((p) => {
        const pTriage = pathwayTriage(patientRecord, p);
        // TDI-703: "In review — {name}" for the holder, Org Admins and
        // Support Users; "In review" for other clinicians (LSC-040). "Ready
        // for triage" once the referral is marked ready.
        const { label, tone } = getPathwayTriageStatusLabel(
          pathwayReviews(patientRecord, p),
          pTriage,
          readyForTriage,
          { showAssigneeName: canForceUnassign || pTriage?.assignedClinicianId === user?.uid },
        );
        return { pathway: p, name: PATHWAY_LABELS[p] || p, status: label, tone };
      })
    : [];

  // Breadcrumbs for patient overview
  const breadcrumbs = [
    { label: 'Home', path: '/rms/provider/dashboard' },
    { label: 'Patient Overview', path: null }
  ];

  // Open the full Holistic Functional Profile (v2) on its dedicated page.
  const handleViewHfp = useCallback(
    () => navigate(`/rms/provider/patient/${patientRecordId}/hfp`),
    [navigate, patientRecordId],
  );

  // TD-208: every matrix cell deep-links into the Clinical HFP view at the
  // matching area (the clinical drill-down page owns the area routing).
  const handleOpenHfpV3Area = useCallback(
    ({ domain, subdomain }) =>
      navigate(
        `/rms/provider/patient/${patientRecordId}/hfp-v3-clinical?domain=${encodeURIComponent(
          domain
        )}&area=${encodeURIComponent(subdomain || '')}`
      ),
    [navigate, patientRecordId],
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/15 flex flex-col">
      <RmsTopNavBar breadcrumbs={breadcrumbs} />

      <div className="flex-1 px-6 sm:px-8 lg:px-12 py-8">

        {/* Loading state */}
        {loading && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <RefreshIcon className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-3" />
            <p className="text-gray-600">Loading patient record...</p>
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="bg-red-50 rounded-xl border border-red-200 p-6 text-center">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* R-038 (TDI-758): a referral from the other service refuses to
            open, by name, with the one honest way out. No data was sent. */}
        {specialismRefusal && (
          <div role="alert" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center max-w-xl mx-auto">
            <p className="text-gray-900 font-medium mb-4">{specialismRefusal}</p>
            <button
              type="button"
              onClick={() => navigate('/rms/provider/dashboard')}
              className="inline-flex items-center justify-center min-h-[44px] px-5 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90"
            >
              Go to dashboard
            </button>
          </div>
        )}

        {/* Consent withdrawn (TDI-475 / TDI-481). A clinician is stopped by
            the blocking message and sent back to the dashboard. The
            non-clinical side may choose to read the record instead, under
            the banner, with every action gone. */}
        <ConsentWithdrawnDialog
          open={withdrawnStop.stopped && !readOnlyWithdrawn && !specialismRefusal}
          onLeave={() => navigate('/rms/provider/dashboard')}
          onViewReadOnly={
            withdrawnStop.reason === 'withdrawn' && isNonClinicalProvider(claims)
              ? () => setReadOnlyWithdrawn(true)
              : undefined
          }
        />
        {readOnlyWithdrawn && patientRecord && <ConsentWithdrawnBanner patientRecord={patientRecord} />}
        <RecordConsentWithdrawalDialog
          open={showRecordWithdrawal}
          onOpenChange={setShowRecordWithdrawal}
          orgId={orgId}
          patientRecordId={patientRecordId}
          childName={patientRecord?.subject_first_name || patientRecord?.firstName || null}
          onRecorded={() => toast.success('Withdrawal recorded. The referral has stopped.')}
        />

        {/* Main content */}
        {!loading && !error && patientRecord && (
          <>
            {/* ═══════════════════════════════════════════════════════════════════
                Patient Header Card
                ═══════════════════════════════════════════════════════════════════ */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-5 py-3 mb-3">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                {/* Left: Patient info — compact single line */}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-2xl font-bold text-gray-900 leading-tight">{patientName}</h1>
                    <ConcernBadge intakeConcern={patientRecord?.intakeConcern} />
                  </div>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-0.5 text-sm text-gray-700 mt-1">
                    {patientSex && <span><span className="font-semibold text-gray-900">Sex:</span> {patientSex}</span>}
                    {patientGender && <span><span className="font-semibold text-gray-900">Gender:</span> {patientGender}</span>}
                    {patientDob && <span><span className="font-semibold text-gray-900">DOB:</span> {formatDateShort(patientDob, '-')}</span>}
                    {patientAge != null && <span><span className="font-semibold text-gray-900">Age:</span> {patientAge}</span>}
                    {educationSetting && <span><span className="font-semibold text-gray-900">Education Setting:</span> {educationSetting}</span>}
                    {patientGpPractice && <span><span className="font-semibold text-gray-900">GP practice (school reported):</span> {patientGpPractice}</span>}
                  </div>
                </div>

                {/* Right: Action buttons. Gone on a withdrawn referral the
                    non-clinical side is reading (TDI-481): no triage, letter
                    or handover action exists for it. */}
                <div className={`flex items-center gap-3${readOnlyWithdrawn ? ' hidden' : ''}`}>
                  {/* Decision + 2nd-triage status badges, named + dated (TD-141,
                      design-aligned). A first rejection shows the rejecting
                      clinician and that a 2nd triage is still outstanding. */}
                  {showPathwayStatusFlags ? (
                    pathwayStatusFlags.map(({ pathway, name, status, tone }) => (
                      <span
                        key={pathway}
                        className={`inline-flex items-center px-4 py-2 text-sm font-semibold rounded-full ${PATHWAY_STATUS_TONE_CLASSES[tone]}`}
                      >
                        {name}: {status}
                      </span>
                    ))
                  ) : decisionOutcome === 'accepted' ? (
                    <span className="inline-flex items-center px-4 py-2 bg-primary/10 text-primary text-sm font-semibold rounded-full">
                      Referral Accepted by: {decisionDeciderName || 'a clinician'}
                      {decisionDecidedAt ? ` on ${formatDateShort(decisionDecidedAt, '-')}` : ''}
                    </span>
                  ) : (decisionOutcome === 'discharged' || firstRejection) ? (
                    <>
                      <span className="inline-flex items-center px-4 py-2 bg-primary/10 text-primary text-sm font-semibold rounded-full">
                        Referral Rejected by: {firstRejection ? firstReviewerName : (decisionDeciderName || 'a clinician')}
                        {(firstRejection?.decidedAt || decisionDecidedAt)
                          ? ` on ${formatDateShort(firstRejection?.decidedAt || decisionDecidedAt, '-')}`
                          : ''}
                      </span>
                      {/* The 2nd-triage badge belongs to the autism dual-review
                          flow only: an ADHD rejection is a single terminal
                          decision (Tom King, 22 Oct), so a completed
                          single-review rejection must not advertise a second
                          triage that will never happen. */}
                      {(firstRejection || triageReviewsArr.length >= 2) && (
                        <span className="inline-flex items-center px-4 py-2 bg-primary/10 text-primary text-sm font-semibold rounded-full">
                          2nd Triage: {
                            triageReviewsArr.length >= 2
                              ? 'Complete'
                              : inTriage
                                // Reviewer names stay hidden from other
                                // clinicians (LSC-040); Support / Org. Admins
                                // see who has the case.
                                ? `Started by ${assignedToMe
                                  ? 'you'
                                  : (canForceUnassign && triage.assignedClinicianName) || 'a clinician'}`
                                : 'Not Started'
                          }
                        </span>
                      )}
                    </>
                  ) : inTriage ? (
                    /* TDI-703: picked up reads as the list does, "In review —
                       {name}", naming the holder only to the holder, Org Admins
                       and Support Users (LSC-040). */
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary/10 text-primary text-sm font-semibold rounded-full">
                      {inReviewLabel(triage.assignedClinicianName, assignedToMe || canForceUnassign)}
                    </span>
                  ) : triageComplete ? (
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-green-50 text-green-700 text-sm font-semibold rounded-full border border-green-200">
                      Triage complete
                    </span>
                  ) : readyForTriage ? (
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary/10 text-primary text-sm font-semibold rounded-full">
                      Ready for triage
                    </span>
                  ) : isReadyForReview ? (
                    /* Forms may be underway or even complete, but the active
                       ready decision (LSC-038/LSC-044) has not been made. */
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-50 text-amber-700 text-sm font-semibold rounded-full border border-amber-200">
                      Not marked ready for triage
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-50 text-amber-700 text-sm font-semibold rounded-full border border-amber-200">
                      Awaiting data
                    </span>
                  )}

                  {/* The consensus pill was removed for go-live (TDI-784,
                      R-053): the platform has no consensus step, so no screen
                      may promise one. The server still records consensusFlag
                      in the audit trail when the two outcomes disagree, shown
                      nowhere; each service runs its own consensus procedure
                      outside the platform. */}

                  {/* Admin handover (TDI-386) — the way back to the guided EMIS
                      handover screen while the referral waits in Admin Handover.
                      Outline style: the one filled CTA on this header stays the
                      triage action. */}
                  {handoverPathway && (
                    <button
                      type="button"
                      onClick={() => navigate(`/rms/provider/patient/${patientRecordId}/handover?pathway=${handoverPathway}`)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-primary text-primary text-sm font-medium rounded-lg hover:bg-primary/5 transition-colors"
                    >
                      Admin handover
                      <ChevronRightIcon className="w-4 h-4" aria-hidden="true" />
                    </button>
                  )}

                  {/* Other Actions — secondary actions (documents, preview, release)
                      consolidated into a dropdown; the primary triage CTA stays to
                      the right (design-aligned). */}
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Other Actions
                        <ChevronDownIcon className="w-4 h-4" />
                      </button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                      <DropdownMenu.Content
                        align="end"
                        sideOffset={6}
                        className="z-50 min-w-[200px] rounded-lg border border-gray-200 bg-white p-1 shadow-lg"
                      >
                        {canStartTriage && !assignedToMe && (
                          <DropdownMenu.Item
                            onSelect={() => navigate(`/rms/provider/patient/${patientRecordId}/triage?preview=1${actingTriagePathway ? `&pathway=${actingTriagePathway}` : ''}`)}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 rounded-md cursor-pointer outline-none hover:bg-gray-50 focus:bg-gray-50"
                          >
                            <SearchIcon className="w-4 h-4 text-gray-400" />
                            Preview forms
                          </DropdownMenu.Item>
                        )}
                        <DropdownMenu.Item
                          onSelect={() => navigate(`/rms/provider/patient/${patientRecordId}/documents${actingTriagePathway ? `?pathway=${actingTriagePathway}` : ''}`)}
                          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 rounded-md cursor-pointer outline-none hover:bg-gray-50 focus:bg-gray-50"
                        >
                          <DocumentTextIcon className="w-4 h-4 text-gray-400" />
                          Go to Documents
                        </DropdownMenu.Item>
                        {/* TDI-476: a parent or carer told the service they are
                            withdrawing. Any provider role may record it; the
                            server stops the referral. Deferred for the same
                            menu-opens-dialog reason as the downloads below. */}
                        {!withdrawnStop.stopped && (
                          <DropdownMenu.Item
                            onSelect={() => { setTimeout(() => setShowRecordWithdrawal(true), 0); }}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-red-700 rounded-md cursor-pointer outline-none hover:bg-red-50 focus:bg-red-50"
                          >
                            Record a withdrawal of consent
                          </DropdownMenu.Item>
                        )}
                        {/* Each pathway's recorded decision as its own PDF for
                            EMIS entry — offered only once that pathway's triage
                            is complete (second-review blinding). */}
                        {triageDecisionDownloads.map(({ pathway, label }) => {
                          const preparing = documentDownloadPendingKey === `decision:${pathway || 'referral'}`;
                          return (
                            <DropdownMenu.Item
                              key={`decision-${pathway || 'referral'}`}
                              disabled={Boolean(documentDownloadPendingKey)}
                              // Deferred: the download now opens the
                              // confidentiality dialog (TDI-484), and two
                              // Radix focus scopes must never be live at once
                              // — the closing menu and the opening modal fight
                              // over focus forever (hangs jsdom, flickers in
                              // browsers). Letting the menu close first and
                              // opening the dialog on the next tick is the
                              // standard menu-opens-dialog idiom.
                              onSelect={() => {
                                setTimeout(() => handleDownloadDecision(pathway), 0);
                              }}
                              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 rounded-md cursor-pointer outline-none hover:bg-gray-50 focus:bg-gray-50 data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed"
                            >
                              <DownloadIcon className="w-4 h-4 text-gray-400" />
                              {preparing ? `Preparing ${label}…` : `Download ${label} (PDF)`}
                            </DropdownMenu.Item>
                          );
                        })}
                        {inTriage && (assignedToMe || canForceUnassign) && !triageViewOnly && (
                          <DropdownMenu.Item
                            disabled={unassigningTriage}
                            onSelect={() => setShowReleaseConfirm(true)}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 rounded-md cursor-pointer outline-none hover:bg-gray-50 focus:bg-gray-50 data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed"
                          >
                            {unassigningTriage ? 'Releasing…' : 'Release triage'}
                          </DropdownMenu.Item>
                        )}
                        {/* B13 / RMS-59: staff-side correction of the nominated
                            parent/carer's details — Org Admin / Support only
                            (backend-enforced too). */}
                        {canForceUnassign && (
                          <DropdownMenu.Item
                            onSelect={() => setShowEditGuardian(true)}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 rounded-md cursor-pointer outline-none hover:bg-gray-50 focus:bg-gray-50"
                          >
                            <UserIcon className="w-4 h-4 text-gray-400" />
                            Edit parent/carer details
                          </DropdownMenu.Item>
                        )}
                      </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                  </DropdownMenu.Root>

                  {/* Triage assignment + server-enforced lock (TD-28/29) and role
                      gating (TD-40). Only clinicians / clinical org admins can start
                      triage; starting it assigns + locks the patient. Others see who
                      currently holds it. Before any of that, the referral must be
                      ready. Completion does that by itself (rmsSubmitForm marks
                      it as the last form lands), so this action appears only
                      while forms are still outstanding, where a person has to
                      say why it is going forward anyway. Any provider user may. */}
                  {!readyForTriage && !anyTriageActivity && userCanMarkReady
                    && (!allRequestedFormsComplete || readinessBlockers.length > 0) ? (
                    <div className="flex flex-col items-end gap-2">
                      {/* The action is for pushing an incomplete referral
                          forward. A complete one is marked by the server, so
                          the button goes - but anything blocking it must still
                          be said, or a referral that cannot proceed shows
                          nothing at all and reads as broken. */}
                      {!allRequestedFormsComplete && (
                        <button
                          type="button"
                          onClick={() => setShowMarkReadyConfirm(true)}
                          disabled={markingReady || readinessBlockers.length > 0}
                          aria-describedby={readinessBlockers.length > 0 ? 'mark-ready-blockers' : undefined}
                          className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          title="A referral can only be triaged once someone marks it ready. This decision is recorded."
                        >
                          Mark ready for triage
                          <ChevronRightIcon className="w-4 h-4" />
                        </button>
                      )}
                      {readinessBlockers.length > 0 && (
                        <ul id="mark-ready-blockers" className="text-left text-sm text-gray-600 list-disc pl-5 max-w-xs">
                          {readinessBlockers.map((blocker) => (
                            <li key={blocker}>{blocker}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : triageViewOnly && canOpenTriageScreens(claims) ? (
                    <>
                      <button
                        type="button"
                        onClick={() => navigate(`/rms/provider/patient/${patientRecordId}/triage${actingTriagePathway ? `?pathway=${actingTriagePathway}` : ''}`)}
                        className="inline-flex items-center gap-2 px-5 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                        title={!decisionChange.allowed
                          ? 'View the recorded triage response. The decision can no longer be changed.'
                          : decisionChange.firstRejection
                            ? FIRST_REJECTION_CHANGEABLE_NOTICE
                            : DECISION_CHANGEABLE_NOTICE}
                      >
                        Show triage
                        <ChevronRightIcon className="w-4 h-4" />
                      </button>
                      {/* TDI-695: the deciding clinician may change their own
                          decision until the letter is signed off. */}
                      {decisionChange.allowed && (
                        <ChangeDecisionButton
                          orgId={orgId}
                          patientRecordId={patientRecordId}
                          pathway={actingTriagePathway}
                          firstRejection={decisionChange.firstRejection}
                          onReopened={() => navigate(`/rms/provider/patient/${patientRecordId}/triage${actingTriagePathway ? `?pathway=${actingTriagePathway}` : ''}`)}
                        />
                      )}
                    </>
                  ) : assignedToOther ? (
                    /* TDI-703: Org Admins and Support Users read the holder in
                       the badge ("Release triage" stays in Other Actions);
                       other clinicians are told only that it is taken. */
                    canForceUnassign ? null : (
                      <span
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-100 text-gray-600 text-sm font-medium rounded-lg border border-gray-200"
                        title="This patient is currently being triaged by another clinician."
                      >
                        Another clinician has picked this up
                      </span>
                    )
                  ) : canStartTriage ? (
                    <button
                      type="button"
                      onClick={assignedToMe
                        ? () => navigate(`/rms/provider/patient/${patientRecordId}/triage${actingTriagePathway ? `?pathway=${actingTriagePathway}` : ''}`)
                        : () => setShowStartConfirm(true)}
                      disabled={startingTriage}
                      className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Starting triage assigns this patient to you. Other clinicians cannot triage them while you are assigned."
                    >
                      {startingTriage ? 'Starting…' : assignedToMe ? 'Resume triage' : 'Start triage'}
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  ) : outOfSpecialism ? (
                    <span
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-100 text-gray-600 text-sm font-medium rounded-lg border border-gray-200"
                      title="This referral is outside your clinical specialism, so it is handled by the other team."
                    >
                      Outside your specialism
                      {getIntakeConcernLabel(patientRecord?.intakeConcern)
                        ? ` (${getIntakeConcernLabel(patientRecord?.intakeConcern)} referral)`
                        : ''}
                    </span>
                  ) : null}

                </div>
              </div>
            </div>

            {/* Urgent risk alert flag (TDI-701): an alert went to the service. */}
            <UrgentRiskAlertFlag orgId={orgId} patientRecordId={patientRecordId} />

            {/* Second-review banner — sits directly under the patient header.
                The original reviewer sees their own decision + rationale; any
                other clinician sees the context without the rationale so their
                second opinion stays independent. */}
            {firstRejection && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 text-left">
                {viewerIsFirstReviewer ? (
                  <>
                    <p className="text-sm font-semibold text-amber-900">
                      Decision submitted. Awaiting second opinion
                    </p>
                    <p className="text-xs text-amber-800 mt-1">
                      {decisionChange.allowed
                        ? FIRST_REJECTION_CHANGEABLE_NOTICE
                        : 'This triage decision has been recorded and can no longer be changed.'} You
                      previously reviewed this referral and decided not to accept. If the second
                      clinician reviewing this patient&rsquo;s referral also rejects, the referral
                      will not be accepted onto the pathway at this stage.
                    </p>
                    {(firstRejection.rationale || '').trim() && (
                      <div className="mt-2 rounded-md bg-white/70 border border-amber-200 p-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                          Your rationale
                        </p>
                        <p className="text-sm text-amber-900 mt-1 whitespace-pre-wrap">
                          {firstRejection.rationale.trim()}
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-amber-900">
                      Second review
                    </p>
                    <p className="text-xs text-amber-800 mt-1">
                      <strong>{firstReviewerName}</strong> reviewed this referral and decided not to
                      accept. If you also reject, the referral will not be accepted onto the
                      pathway at this stage.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Decision flag (TD-141) — once triage is complete, state the
                recorded outcome and who decided it, directly under the header. */}
            {decisionOutcome && (
              <div
                className={`border rounded-xl p-4 mb-4 text-left ${
                  decisionOutcome === 'accepted'
                    ? 'bg-green-50 border-green-200'
                    : 'bg-rose-50 border-rose-200'
                }`}
              >
                <p
                  className={`text-sm font-semibold ${
                    decisionOutcome === 'accepted' ? 'text-green-900' : 'text-rose-900'
                  }`}
                >
                  {decisionOutcome === 'accepted'
                    ? 'Referral accepted at triage'
                    : 'Referral not accepted onto the pathway at this stage'}
                </p>
                {(decisionDeciderName || decisionDecidedAt) && (
                  <p
                    className={`text-xs mt-1 ${
                      decisionOutcome === 'accepted' ? 'text-green-800' : 'text-rose-800'
                    }`}
                  >
                    Decision by{' '}
                    <span className="font-semibold">{decisionDeciderName || 'a clinician'}</span>
                    {decisionDecidedAt ? <> on {formatDateShort(decisionDecidedAt, '-')}</> : null}.
                  </p>
                )}
              </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════════
                Main Content Layout: 4-Column Grid with Mega HFP Block
                ═══════════════════════════════════════════════════════════════════
                
                Layout Structure:
                ┌─────────────────┬─────────────────┬─────────────────────────────┐
                │ People Involved │ Risk Strat.     │                             │
                │ in This         │ & Prioritisation│     Holistic Functional     │
                │ Referral        │                 │     Profile (Mega Block)    │
                ├─────────────────┼─────────────────┤                             │
                │ Neurodevelop-   │ Mental Health   │                             │
                │ mental Rating   │ Rating Scales   │                             │
                │ Scales          │                 │                             │
                └─────────────────┴─────────────────┴─────────────────────────────┘
                
                Column 1: People Involved (row 1), Neurodevelopmental Scales (row 2)
                Column 2: Risk Stratification (row 1), Mental Health Scales (row 2)
                Columns 3-4: HFP spanning both rows (row-span-2, col-span-2)
                ═══════════════════════════════════════════════════════════════════ */}
            <div className="grid grid-cols-4 gap-4 auto-rows-min items-start">
              
              {/* Column 1, Row 1: People Involved in This Referral */}
              <PeopleInvolvedCard
                formInstances={formInstances}
                triageAssigned={triageAssigned}
                respondentNames={respondentNames}
                onOpenDialog={handleOpenFormProgressDialog}
                schoolPhase={schoolPhase}
                intakeConcern={intakeConcern}
                patientRecord={patientRecord}
              />

              {/* Column 2, Row 1: Risk Stratification. Display-only gate: some
                  orgs hide the automated surfacing (still computed/stored
                  server-side). See src/rms/constants/orgFeatures.js. */}
              {isRiskStratificationVisible(orgId) && (
                <RiskStratificationCard
                  riskData={riskStratification}
                  orgId={orgId}
                  patientRecordId={patientRecordId}
                  respondentNames={respondentNames}
                  hfpPainOverride={hfpV3PainOverride}
                  // TD-51 (A18): only the self-assigned, committed triage clinician
                  // may flag the manual-review statements that inform priority.
                  canReview={assignedToMe}
                />
              )}

              {/* Columns 3-4, Rows 1-2: Holistic Functional Profile clinical summary
                  (v3 home-vs-school matrix when the record is scored under v3;
                  the v2 summary card otherwise — older records are untouched) */}
              {/* Secondary pupils never do the HFP (TD-129/TD-137), so there is
                  nothing to share and the card says so before anything else.
                  TDI-483: otherwise the profile is the family's to share. With
                  no yes, or SEND consent withdrawn, the card says so and shows
                  nothing of the profile itself; a yes with the forms not yet
                  done shows the pending lock, as it did before the question. */}
              {schoolPhase === 'secondary' ? (
                <HfpV2Card isSecondary className="col-span-2 row-span-2" />
              ) : !profileShared ? (
                profileAwaitingCompletion(patientRecord) ? (
                  <HfpV2Card className="col-span-2 row-span-2" />
                ) : (
                  <HfpNotSharedCard className="col-span-2 row-span-2" />
                )
              ) : hfpInstrument === 'v4' ? (
                <HfpV4Card onViewProfile={handleViewHfp} className="col-span-2 row-span-2" />
              ) : hfpV3?.scoring ? (
                <HfpV3Card
                  scoring={hfpV3.scoring}
                  onViewProfile={handleViewHfp}
                  onOpenArea={handleOpenHfpV3Area}
                  className="col-span-2 row-span-2"
                />
              ) : (
                <HfpV2Card
                  scoring={hfpV2?.scoring}
                  onViewProfile={handleViewHfp}
                  className="col-span-2 row-span-2"
                />
              )}

              {/* Rating Scales (ADHD SNAP-IV; locked for autism-only), with the
                  Service User Uploads card directly under it (TDI-602 FR1).
                  The two share one grid cell, so the card stays under Rating
                  scales in whichever column the grid puts them: the right-hand
                  column for BTH (risk card hidden), the first column where the
                  risk card shows. The card lists the first five documents the
                  school or the family attached, then links to the Documents
                  page for the rest. */}
              <div className="flex flex-col gap-4 min-w-0">
                <NeurodevelopmentalScalesCard
                  formInstances={formInstances}
                  onViewDetails={handleViewDetails}
                  isAsdOnly={String(patientRecord?.intakeConcern || '').toLowerCase() === 'asd'}
                />
                <ServiceUserUploadsPanel
                  orgId={orgId}
                  patientRecordId={patientRecordId}
                  patientRecord={patientRecord}
                  variant="card"
                  onSeeAll={() => navigate(`/rms/provider/patient/${patientRecordId}/documents`)}
                />
              </div>

              {/* Column 2, Row 2: Referral Details (referral metadata at a glance) */}
              <ReferralDetailsCard
                referredForLabel={getIntakeConcernLabel(patientRecord?.intakeConcern)}
                referralStarted={referralDate}
                referralReceived={referralReceivedDate}
                referrerName={referrerName}
                referrerRole={patientRecord?.referrer?.roleTitle || patientRecord?.referrer?.relationshipLabel || null}
                guardianName={
                  respondentNames?.guardian
                    ? `${respondentNames.guardian.firstName || ''} ${respondentNames.guardian.lastName || ''}`.trim() || null
                    : null
                }
                guardianRelationship={respondentNames?.guardian?.relationshipLabel || null}
                reasonAgreement={getReferralReasonAgreement(formInstances)}
              />
            </div>
          </>
        )}
      </div>

      {/* Form details dialog */}
      <RmsFormDetailsDialog
        isOpen={detailsDialog.isOpen}
        onClose={handleCloseDetails}
        formKey={detailsDialog.formKey}
        formTitle={detailsDialog.formTitle}
        formInstance={detailsDialog.formInstance}
        orgId={orgId}
        role={detailsDialog.role}
        initiatedBy={patientRecord?.referrerType || 'school'}
        onDownload={
          isExportableFormKey(detailsDialog.formKey)
          && Object.keys(detailsDialog.formInstance?.answers || {}).length > 0
            ? () => downloadForm(detailsDialog.instanceId)
            : undefined
        }
        downloading={documentDownloadPendingKey === `form:${detailsDialog.instanceId}`}
        downloadDisabled={Boolean(documentDownloadPendingKey)}
      />

      {/* Assign triage forms dialog (dev/test feature) */}
      <AssignTriageFormsDialog
        isOpen={showAssignTriageDialog}
        onOpenChange={setShowAssignTriageDialog}
        patientRecordId={patientRecordId}
        onSuccess={handleTriageAssigned}
      />

      {/* The HFP profile lives on its own page (/rms/provider/patient/:id/hfp);
          the overview shows the v2 clinical summary square (HfpV2Card). */}

      {/* Form progress dialog - shows detailed forms for a respondent */}
      <FormProgressDialog
        isOpen={formProgressDialog.isOpen}
        onClose={handleCloseFormProgressDialog}
        role={formProgressDialog.role}
        formInstances={formInstances}
        triageAssigned={triageAssigned}
        orgId={orgId}
        patientRecordId={patientRecordId}
        respondentNames={respondentNames}
        schoolPhase={schoolPhase}
        intakeConcern={intakeConcern}
        onAutoComplete={handleAutoComplete}
        onViewDetails={handleViewDetails}
        onDownload={downloadForm}
        downloadPendingKey={documentDownloadPendingKey}
        patientRecord={patientRecord}
      />

      {/* B13 / RMS-59: staff-side editing of the nominated parent/carer's details. */}
      <RmsEditGuardianDialog
        isOpen={showEditGuardian}
        onClose={() => setShowEditGuardian(false)}
        orgId={orgId}
        patientRecordId={patientRecordId}
        onSaved={() => setParticipantsRefresh((n) => n + 1)}
      />

      {/* Start-triage confirmation — makes the assignment + lock explicit before
          a clinician commits to triaging the patient. */}
      {/* TD-244: releasing a triage discards unsubmitted decision progress. */}
      {showReleaseConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true" aria-labelledby="release-triage-title">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-md p-6 text-left">
            <h2 id="release-triage-title" className="text-base font-semibold text-gray-900">
              Release triage{patientName ? ` for ${patientName}` : ''}?
            </h2>
            <p className="text-sm text-gray-600 mt-2">
              {assignedToOther
                ? `This releases ${triage?.assignedClinicianName || 'the assigned clinician'}'s triage and returns the referral to the triage pool. Any decision progress they have not submitted will be discarded and will not be visible to the next reviewer, who starts from a blank slate.`
                : 'This returns the referral to the triage pool for another reviewer. Any triage decision progress you have not submitted will be discarded and will not be visible to the next reviewer, who starts from a blank slate.'}
            </p>
            {/* LSC-033: releasing someone else's triage must never be silent —
                the reason is required and is emailed to the affected clinician. */}
            {assignedToOther && (
              <label className="mt-4 block text-sm font-medium text-gray-900">
                Why are you releasing this triage?
                <span className="mt-0.5 block text-xs font-normal text-gray-500">
                  The assigned clinician will be notified by email with this reason.
                </span>
                <textarea
                  value={releaseReason}
                  onChange={(e) => setReleaseReason(e.target.value)}
                  rows={3}
                  className="mt-1.5 block w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </label>
            )}
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowReleaseConfirm(false); setReleaseReason(''); }}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={unassigningTriage || (assignedToOther && !releaseReason.trim())}
                onClick={() => handleUnassignTriage()}
                className="px-5 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {unassigningTriage ? 'Releasing…' : 'Release triage'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ready-for-triage decision (LSC-038/LSC-044) — the active, audited step
          that moves a referral into the triage queue. When forms are incomplete
          the decision-maker must record why it is being put forward anyway. */}
      {showMarkReadyConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true" aria-labelledby="mark-ready-title">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-md p-6 text-left">
            <h2 id="mark-ready-title" className="text-base font-semibold text-gray-900">
              Mark {patientName || 'this referral'} as ready for triage?
            </h2>
            {/* Only reachable with forms outstanding: a referral whose forms
                are all in is marked ready by the server the moment the last one
                is submitted, and offers no button. */}
            <p className="text-sm text-gray-600 mt-2">
              Some forms are still incomplete. You can put this referral forward
              anyway, and the triage team will see what has been filled in so far.
              Your reason is recorded.
            </p>
            <label htmlFor="mark-ready-reason" className="block text-sm font-medium text-gray-900 mt-4">
              Why is it going forward now?
            </label>
            <p className="text-xs text-gray-500 mt-1">
              For example, you are waiting on a report from the school.
            </p>
            <textarea
              id="mark-ready-reason"
              value={markReadyReason}
              onChange={(e) => setMarkReadyReason(e.target.value)}
              rows={3}
              maxLength={2000}
              className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowMarkReadyConfirm(false); setMarkReadyReason(''); }}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={markingReady || (!allRequestedFormsComplete && !markReadyReason.trim())}
                onClick={() => handleMarkReadyForTriage()}
                className="px-5 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {markingReady ? 'Marking…' : 'Mark ready for triage'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showStartConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true" aria-labelledby="start-triage-title">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-md p-6 text-left">
            <h2 id="start-triage-title" className="text-base font-semibold text-gray-900">
              Start triage for {patientName}?
            </h2>
            <p className="text-sm text-gray-600 mt-2">
              This assigns the patient to you and locks the referral to other clinicians until you
              release it or complete a decision.
            </p>
            {/* TD-173: a combined referral is triaged per pathway. When no
                pathway is implied by the page context or the clinician's own
                specialism (a both-specialism clinician), ask which pathway
                is being triaged rather than sending a request the server
                must reject. */}
            {isCombinedConcern(patientRecord?.intakeConcern) && !actingTriagePathway ? (
              <>
                <p className="text-sm text-gray-600 mt-2">
                  This is a combined referral. Each pathway is triaged separately. Which
                  pathway are you starting?
                </p>
                <div className="mt-5 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowStartConfirm(false)}
                    className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  {pathwaysForConcern(patientRecord?.intakeConcern).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => handleStartTriage(p)}
                      className="px-5 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 shadow-sm"
                    >
                      Start {PATHWAY_LABELS[p] || p} triage
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="mt-5 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowStartConfirm(false)}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleStartTriage()}
                  className="px-5 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 shadow-sm"
                >
                  Start triage
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* The confidentiality confirm every download passes through (TDI-484). */}
      {downloadConfirmDialog}
    </div>
  );
}
